// Reference to the global command queue for asynchronous execution of GPT-related calls.
var googletag = googletag || {};
googletag.cmd = googletag.cmd || [];


// Reference to the global command queue for asynchronous execution of Prebid-related calls.
var pbjs = pbjs || {};
pbjs.que = pbjs.que || [];

// =============================================================================
// TCF CMP WATCHDOG
// =============================================================================
//
// Background: a consentmanager.net CMP is embedded in this site's templates.
// When its remote configuration fails to load, the CMP answers every TCF query
// with cmpStatus "loading" forever: no banner is rendered, no consent string
// is ever produced, and gdprApplies stays true. GPT and Prebid are both
// consent-aware, so in that state neither of them sends a single ad request
// and every slot on the site goes dark. This happened for real on 03.09.2026
// and cost a full ad outage.
//
// What the failure actually looks like (measured 04.09.2026): the CMP core
// script loads and its first configuration call (/delivery/cmp.php) completes
// normally, but the answer carries no configuration -- the account's designs
// are all geo-targeted and none is marked as fallback, so a visitor whose
// country matches no design gets nothing. On a healthy load the design file
// (/delivery/customdata/...) is requested within a few milliseconds of
// cmp.php finishing; on the broken load nothing ever follows cmp.php.
//
// What this watchdog does:
//
//   1. It wraps window.__tcfapi in a transparent pass-through proxy. While
//      the CMP behaves normally, the proxy changes nothing: every call and
//      every event goes straight through, and consent behavior is entirely
//      the CMP's.
//
//   2. It watches the CMP's own network activity (the design-file <script>
//      element in the DOM, and the browser's resource timeline). If cmp.php
//      has completed and no design file has been requested for
//      CMP_NO_DESIGN_CONFIRM_MILLISECONDS afterwards, the
//      CMP is in the broken state described above and the watchdog takes
//      over. This is a positive detection of the failure, not a timeout, so
//      it fires early (about 2 seconds into the page) for a broken visitor and
//      NEVER fires for a visitor whose CMP is merely slow but progressing.
//
//   3. A visitor for whom the CMP IS progressing (design file requested, or
//      the CMP's own status advanced, or a real "cmpuishown" / "tcloaded"
//      event arrived) is left entirely to the real CMP. That includes EU
//      visitors, who must see the banner and whose ads must wait for their
//      choice. The watchdog stands down for good on any such signal.
//
//   4. As a last resort, if the CMP shows no progress at all for
//      CMP_WATCHDOG_MAX_WAIT_MILLISECONDS (its scripts never loaded, the
//      vendor is entirely unreachable), the watchdog also takes over, so a
//      genuine vendor outage still cannot black out the site for the
//      non-EEA audience.
//
//   5. Before taking over it checks the visitor's device timezone. A device
//      set to an unambiguously EEA/CH/UK timezone (Berlin, Vienna, Zurich,
//      Paris, ...) is never answered synthetically: for that visitor the
//      watchdog does nothing, and if the CMP cannot serve them they get no
//      ads rather than ads without consent. Timezones that Serbian devices
//      commonly report (the Windows "Belgrade, Bratislava, Budapest,
//      Ljubljana, Prague" group maps to Europe/Budapest on many machines)
//      are deliberately NOT on that list, so a Serbian visitor is never
//      denied ads by this check. It is a fail-safe, not a geo lookup.
//
//   6. On takeover, every TCF consumer that registered through the proxy
//      (GPT, Prebid) immediately receives a synthetic final answer with
//      gdprApplies = false, and every later TCF call is answered the same
//      way. gdprApplies = false is the correct resolution for this site's
//      overwhelmingly Serbian (non-EEA) audience and is exactly how the
//      working consentmanager instances on this network resolve for them.
//
//   7. It is self-healing in both directions. If the account is fixed, the
//      design file arrives on every load and the watchdog never fires. If the
//      real CMP asserts itself after a takeover (a genuine "cmpuishown" or
//      final event), the proxy returns to pass-through, the synthetic answers
//      stop, and the GPT recovery refresh is never issued.
//
// Known limitation, accepted: a consumer that registered its listener
// directly with the broken CMP BEFORE this wrapper executed cannot be
// notified through the proxy; the consentmanager registry rescue below
// covers that case for this vendor specifically.
//
// Debugging: window.__cmpWatchdog exposes
// { wrapped, takenOver, cmpFinal, cmpHealthy, takeoverReason, timezone, timezoneBlocked }.

// After the CMP's configuration call (cmp.php) has completed, how long to wait
// for the design file request before concluding the configuration was empty.
// On a healthy load the design file follows within ~10 ms; 750 ms is a very
// wide margin.
var CMP_NO_DESIGN_CONFIRM_MILLISECONDS = 750;

// Absolute ceiling: if the CMP has shown no progress of any kind for this
// long after the proxy was installed, take over anyway (vendor unreachable).
// Generous on purpose: an EU visitor on a slow connection may need several
// seconds just for cmp.php (measured 2.6 s from Germany), and taking over
// before that would bypass their banner. Serbian visitors on the broken
// instance are not affected by this value at all -- their takeover comes from
// the empty-configuration signature at ~2 s.
var CMP_WATCHDOG_MAX_WAIT_MILLISECONDS = 8000;

// How often the watchdog re-evaluates the CMP's progress.
var CMP_WATCHDOG_POLL_MILLISECONDS = 250;

// Device timezones that identify an EEA/CH/UK visitor beyond reasonable doubt.
// The synthetic "GDPR does not apply" answer is never given to these. The
// list intentionally omits every zone a Serbian device might report:
// Europe/Belgrade itself, and the Windows group-default zones Europe/Budapest
// (Belgrade/Bratislava/Budapest/Ljubljana/Prague group) and Europe/Warsaw
// (Sarajevo/Skopje/Warsaw/Zagreb group), plus Prague, Bratislava, Ljubljana
// and Zagreb which share those groups.
var CMP_WATCHDOG_EEA_DEVICE_TIMEZONES = [
    'Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich', 'Europe/Amsterdam',
    'Europe/Brussels', 'Europe/Luxembourg', 'Europe/Paris', 'Europe/Madrid',
    'Europe/Rome', 'Europe/Malta', 'Europe/Copenhagen', 'Europe/Stockholm',
    'Europe/Oslo', 'Europe/Helsinki', 'Europe/Athens', 'Europe/Bucharest',
    'Europe/Sofia', 'Europe/Vilnius', 'Europe/Riga', 'Europe/Tallinn',
    'Europe/Dublin', 'Europe/London', 'Europe/Lisbon', 'Atlantic/Reykjavik',
    'Atlantic/Madeira', 'Atlantic/Azores', 'Atlantic/Canary', 'Europe/Nicosia',
    'Asia/Nicosia', 'Europe/Vaduz'
];

// The CMP stub is normally installed by the template before this file runs,
// but load order is not guaranteed. If window.__tcfapi is not there yet, the
// watchdog polls for it this often, for at most this long, then gives up
// (no CMP on the page means nothing blocks ad serving and there is nothing
// to guard).
var CMP_APPEARANCE_POLL_MILLISECONDS = 200;
var CMP_APPEARANCE_MAX_WAIT_MILLISECONDS = 10000;

(function installCmpWatchdog() {

    // Read-only state for debugging from the console / remote sessions.
    var watchdogState = {
        wrapped: false,
        takenOver: false,
        cmpFinal: false,
        cmpHealthy: false,
        takeoverReason: null,
        timezone: null,
        timezoneBlocked: false
    };
    window.__cmpWatchdog = watchdogState;

    // The CMP's own __tcfapi function once the proxy is installed.
    var realTcfApi = null;

    // Every listener registered through the proxy, so the watchdog can answer
    // them itself on takeover. Each entry is { callback, syntheticListenerId }.
    var proxiedListeners = [];

    // Synthetic listener ids start high so they can never collide with ids
    // handed out by the real CMP.
    var nextSyntheticListenerId = 100000;

    // The progress poll timer, and when the proxy was installed (poll clock).
    var progressPollTimer = null;
    var proxyInstalledAtMilliseconds = 0;

    function logWatchdog(message) {
        try {
            console.log('[ads.js CMP watchdog] ' + message);
        } catch (ignored) {}
    }

    function nowMilliseconds() {
        return new Date().getTime();
    }

    // True while the watchdog's synthetic answers are in force: it has taken
    // over AND the real CMP has neither finished nor asserted itself since.
    function syntheticAnswersActive() {
        return watchdogState.takenOver && !watchdogState.cmpFinal && !watchdogState.cmpHealthy;
    }

    // -------------------------------------------------------------------------
    // Device timezone fail-safe (see point 5 in the header comment).
    // -------------------------------------------------------------------------
    function readDeviceTimezone() {
        try {
            var intlObject = window.Intl || (typeof Intl !== 'undefined' ? Intl : null);
            if (!intlObject || typeof intlObject.DateTimeFormat !== 'function') {
                return null;
            }
            var resolved = intlObject.DateTimeFormat().resolvedOptions();
            if (resolved && typeof resolved.timeZone === 'string' && resolved.timeZone.length > 0) {
                return resolved.timeZone;
            }
        } catch (timezoneError) {
            return null;
        }
        return null;
    }

    function deviceLooksEea() {
        var timezone = readDeviceTimezone();
        watchdogState.timezone = timezone;
        if (timezone === null) {
            // Unknown timezone (very old browser): cannot tell, do not block.
            return false;
        }
        var zoneIndex;
        for (zoneIndex = 0; zoneIndex < CMP_WATCHDOG_EEA_DEVICE_TIMEZONES.length; zoneIndex++) {
            if (CMP_WATCHDOG_EEA_DEVICE_TIMEZONES[zoneIndex] === timezone) {
                return true;
            }
        }
        return false;
    }

    // -------------------------------------------------------------------------
    // CMP progress detection through the resource timeline and the CMP's own
    // public state (see points 2-4 in the header comment).
    // -------------------------------------------------------------------------

    // Returns { configCallCompletedAt: ms-since-page-start or null,
    //           designFileRequested: bool }.
    //
    // The design file is requested by the CMP core as an async <script>
    // element appended to the document the moment cmp.php has executed
    // (measured: 2 ms later). Its download can take several seconds on a slow
    // connection, and a resource-timeline entry only exists once a download
    // has COMPLETED -- so an in-flight design file is invisible in the
    // timeline. The DOM is therefore checked first: a <script> whose src is a
    // consentmanager /delivery/customdata/ URL means the configuration
    // arrived and a design is being fetched, i.e. the CMP is working for this
    // visitor, whether or not the file has finished downloading yet.
    function inspectCmpNetworkActivity() {
        var result = { configCallCompletedAt: null, designFileRequested: false };
        try {
            var scriptElements = document.getElementsByTagName('script');
            var scriptIndex;
            for (scriptIndex = 0; scriptIndex < scriptElements.length; scriptIndex++) {
                var scriptSource = String(scriptElements[scriptIndex].src || '');
                if (scriptSource.indexOf('consentmanager') !== -1 && scriptSource.indexOf('/delivery/customdata/') !== -1) {
                    result.designFileRequested = true;
                    break;
                }
            }
        } catch (domError) {
            // fall through to the timeline check
        }
        try {
            if (!window.performance || typeof window.performance.getEntriesByType !== 'function') {
                return result;
            }
            var resourceEntries = window.performance.getEntriesByType('resource');
            var entryIndex;
            for (entryIndex = 0; entryIndex < resourceEntries.length; entryIndex++) {
                var resourceName = String(resourceEntries[entryIndex].name);
                if (resourceName.indexOf('consentmanager') === -1) {
                    continue;
                }
                if (resourceName.indexOf('/delivery/cmp.php') !== -1) {
                    // responseEnd is exposed for cross-origin resources even
                    // without Timing-Allow-Origin; a still-in-flight request
                    // does not appear in the timeline at all.
                    var responseEnd = resourceEntries[entryIndex].responseEnd;
                    if (typeof responseEnd === 'number' && responseEnd > 0) {
                        if (result.configCallCompletedAt === null || responseEnd < result.configCallCompletedAt) {
                            result.configCallCompletedAt = responseEnd;
                        }
                    }
                }
                if (resourceName.indexOf('/delivery/customdata/') !== -1 || resourceName.indexOf('/delivery/info/') !== -1) {
                    result.designFileRequested = true;
                }
            }
        } catch (performanceError) {
            return result;
        }
        return result;
    }

    // The consentmanager core exposes window.cmpmngr.status: it stays at 1 in
    // the broken state and advances (4 = banner shown, 6 = finished) once the
    // configuration has arrived. Any advance beyond 1 is progress.
    function cmpVendorStatusAdvanced() {
        try {
            var cmpVendorObject = window.cmpmngr;
            if (cmpVendorObject && typeof cmpVendorObject.status === 'number' && cmpVendorObject.status > 1) {
                return true;
            }
        } catch (statusError) {
            return false;
        }
        return false;
    }

    // Milliseconds since the page's time origin, on the same clock as the
    // resource timeline entries.
    function millisecondsSincePageStart() {
        try {
            if (window.performance && typeof window.performance.now === 'function') {
                return window.performance.now();
            }
        } catch (performanceError) {
            return 0;
        }
        return 0;
    }

    // Marks the CMP as genuinely working for this visitor and stands the
    // watchdog down permanently. Called on any progress signal.
    function markCmpHealthy(reason) {
        if (watchdogState.cmpHealthy) {
            return;
        }
        watchdogState.cmpHealthy = true;
        stopProgressPoll();
        if (watchdogState.takenOver) {
            logWatchdog('real CMP asserted itself after a takeover (' + reason + '); synthetic answers withdrawn, proxy back to pass-through.');
        } else {
            logWatchdog('CMP is progressing on its own (' + reason + '); watchdog stood down.');
        }
    }

    function stopProgressPoll() {
        if (progressPollTimer !== null) {
            clearInterval(progressPollTimer);
            progressPollTimer = null;
        }
    }

    // One evaluation of the CMP's progress; runs every
    // CMP_WATCHDOG_POLL_MILLISECONDS until a decision is reached.
    function evaluateCmpProgress() {
        if (watchdogState.cmpFinal || watchdogState.cmpHealthy || watchdogState.takenOver) {
            stopProgressPoll();
            return;
        }

        if (cmpVendorStatusAdvanced()) {
            markCmpHealthy('cmpmngr.status advanced past 1');
            return;
        }

        var networkActivity = inspectCmpNetworkActivity();
        if (networkActivity.designFileRequested) {
            markCmpHealthy('design file requested');
            return;
        }

        var elapsedSinceInstall = nowMilliseconds() - proxyInstalledAtMilliseconds;

        if (networkActivity.configCallCompletedAt !== null) {
            var sinceConfigCall = millisecondsSincePageStart() - networkActivity.configCallCompletedAt;
            if (sinceConfigCall >= CMP_NO_DESIGN_CONFIRM_MILLISECONDS) {
                takeOver('cmp.php completed ' + Math.round(sinceConfigCall) + 'ms ago and no design file followed (empty configuration for this visitor)');
                return;
            }
        }

        if (elapsedSinceInstall >= CMP_WATCHDOG_MAX_WAIT_MILLISECONDS) {
            takeOver('no CMP progress of any kind within ' + CMP_WATCHDOG_MAX_WAIT_MILLISECONDS + 'ms (vendor unreachable)');
            return;
        }
    }

    // A minimal but complete TCData object representing "GDPR does not apply".
    // Consumers only read gdprApplies/eventStatus/cmpStatus in this case, but
    // the usual containers are present so nothing trips over a missing field.
    function buildSyntheticTcData() {
        return {
            // Marker so the watchdog's own observers can tell a synthetic
            // answer from a genuine CMP recovery; harmless extra field for
            // every other TCF consumer.
            syntheticWatchdogAnswer: true,
            tcString: '',
            tcfPolicyVersion: 4,
            cmpId: 31,
            cmpVersion: 1,
            gdprApplies: false,
            eventStatus: 'tcloaded',
            cmpStatus: 'loaded',
            isServiceSpecific: true,
            useNonStandardTexts: false,
            publisherCC: 'RS',
            purposeOneTreatment: false,
            listenerId: null,
            purpose: { consents: {}, legitimateInterests: {} },
            vendor: { consents: {}, legitimateInterests: {} },
            specialFeatureOptins: {},
            publisher: {
                consents: {},
                legitimateInterests: {},
                customPurpose: { consents: {}, legitimateInterests: {} },
                restrictions: {}
            }
        };
    }

    function answerListenerSynthetically(listenerEntry) {
        var syntheticData = buildSyntheticTcData();
        syntheticData.listenerId = listenerEntry.syntheticListenerId;
        try {
            listenerEntry.callback(syntheticData, true);
        } catch (listenerError) {
            logWatchdog('a TCF listener threw while receiving the synthetic answer: ' + listenerError);
        }
    }

    // After a takeover, the proxy can only answer consumers that registered
    // THROUGH it. A consumer that registered before this file executed (GPT's
    // pubads implementation wins that race on some page loads) left its
    // callback inside the real CMP's own registry, forever waiting -- the
    // proxy cannot reach it. For the consentmanager CMP specifically, that
    // registry is publicly reachable at window.cmpmngr.api.events as entries
    // of shape { type: 'tcfv2', listener: function, id: number }, so the
    // watchdog answers those held listeners directly as well. Every step is
    // guarded: any unexpected shape (different CMP vendor, changed internals
    // after a consentmanager update) simply skips the rescue and the proxy
    // path still covers consumers that registered normally. A consumer that
    // registered through the proxy also appears in this registry and will
    // receive the synthetic answer twice; TCF listeners are event streams and
    // both GPT and Prebid handle a repeated final event idempotently.
    // Registry entries already answered, keyed by the CMP's own listener id, so
    // retries never answer the same consumer twice.
    var rescuedRegistryIds = {};

    function rescueCmpHeldListeners() {
        var rescuedCount = 0;
        try {
            var cmpVendorObject = window.cmpmngr;
            if (!cmpVendorObject || !cmpVendorObject.api || !cmpVendorObject.api.events || typeof cmpVendorObject.api.events.forEach !== 'function') {
                return 0;
            }
            cmpVendorObject.api.events.forEach(function (registryEntry) {
                if (!registryEntry || registryEntry.type !== 'tcfv2' || typeof registryEntry.listener !== 'function') {
                    return;
                }
                if (rescuedRegistryIds[registryEntry.id] === true) {
                    return;
                }
                rescuedRegistryIds[registryEntry.id] = true;
                var syntheticData = buildSyntheticTcData();
                syntheticData.listenerId = registryEntry.id;
                try {
                    registryEntry.listener(syntheticData, true);
                    rescuedCount = rescuedCount + 1;
                } catch (listenerError) {
                    logWatchdog('a CMP-held TCF listener threw while receiving the synthetic answer: ' + listenerError);
                }
            });
        } catch (rescueError) {
            logWatchdog('CMP-held listener rescue skipped: ' + rescueError);
        }
        return rescuedCount;
    }

    // The registry (window.cmpmngr) belongs to the CMP's full code, which loads
    // over the network and can appear AFTER the takeover fired -- and consumers
    // held in the CMP's stub queue only land in the registry when that code
    // processes the queue. A single rescue pass at takeover therefore misses
    // late arrivals (verified live on 03.09.2026: same build freed GPT on one
    // site and missed it on the other purely by load timing). So the rescue
    // repeats every second for a while after takeover, answering each newly
    // appeared captive listener exactly once, and stops early if the CMP
    // genuinely recovers or asserts itself.
    var CMP_RESCUE_RETRY_INTERVAL_MILLISECONDS = 1000;
    var CMP_RESCUE_MAX_DURATION_MILLISECONDS = 30000;


    // Ad requests that GPT attempted WHILE it was still consent-blocked are
    // dropped, not queued -- freeing GPT afterwards does not resend them
    // (verified live on 03.09.2026: one site's request timing landed after the
    // rescue and rendered, the other site's landed before it and stayed blank
    // with zero gampad requests). So after the first rescue pass that actually
    // freed listeners, wait a moment and check whether ANY ad request has left
    // the page; if none has, issue a single recovery refresh of all GPT slots.
    // The completed Prebid auctions' targeting is already set on the slots, so
    // the recovery request serves with full header-bidding competition.
    // Never issued once the real CMP has asserted itself: for that visitor
    // ad requests must wait for the real consent outcome.
    var gptRedispatchScheduled = false;

    function scheduleGptRedispatchIfSilent() {
        if (gptRedispatchScheduled) {
            return;
        }
        gptRedispatchScheduled = true;
        setTimeout(function () {
            try {
                if (!syntheticAnswersActive()) {
                    logWatchdog('real CMP is in charge; recovery refresh not issued.');
                    return;
                }
                var observedAdRequestCount = 0;
                try {
                    var resourceEntries = [];
                    if (window.performance && typeof window.performance.getEntriesByType === 'function') {
                        resourceEntries = window.performance.getEntriesByType('resource');
                    }
                    var entryIndex;
                    for (entryIndex = 0; entryIndex < resourceEntries.length; entryIndex++) {
                        if (String(resourceEntries[entryIndex].name).indexOf('gampad/ads') !== -1) {
                            observedAdRequestCount = observedAdRequestCount + 1;
                        }
                    }
                } catch (performanceError) {
                    observedAdRequestCount = 0;
                }
                if (observedAdRequestCount > 0) {
                    logWatchdog('GPT already dispatched ' + observedAdRequestCount + ' ad request(s); no recovery refresh needed.');
                    return;
                }
                if (window.googletag && window.googletag.cmd) {
                    window.googletag.cmd.push(function () {
                        // Recover only the slots that were meant to load with the
                        // page. A lazy-loaded slot (div class "lazyAd") has not been
                        // requested yet and will be requested by the lazy-load
                        // observer when it scrolls into range; refreshing it here
                        // would send an out-of-view request without Prebid
                        // targeting and then a second request later. (Until
                        // 05.09.2026 this refreshed every defined slot.)
                        var slotsToRecover = [];
                        var managedSlots = window.googletag.pubads().getSlots();
                        var slotIndex;
                        for (slotIndex = 0; slotIndex < managedSlots.length; slotIndex++) {
                            var managedSlot = managedSlots[slotIndex];
                            var slotElement = null;
                            try {
                                slotElement = document.getElementById(managedSlot.getSlotElementId());
                            } catch (lookupError) {
                                slotElement = null;
                            }
                            var isLazyLoaded = false;
                            if (slotElement && slotElement.classList && typeof slotElement.classList.contains === 'function') {
                                isLazyLoaded = slotElement.classList.contains('lazyAd');
                            }
                            if (!isLazyLoaded) {
                                slotsToRecover.push(managedSlot);
                            }
                        }
                        if (slotsToRecover.length > 0) {
                            window.googletag.pubads().refresh(slotsToRecover);
                        }
                    });
                    logWatchdog('GPT sent no ad requests while consent was blocked; issued a one-time recovery refresh of the non-lazy slots.');
                }
            } catch (redispatchError) {
                logWatchdog('GPT recovery refresh skipped: ' + redispatchError);
            }
        }, 1500);
    }

    function startCmpRegistryRescueLoop() {
        var firstPassCount = rescueCmpHeldListeners() + rescueGppRegistryListeners();
        if (firstPassCount > 0) {
            logWatchdog('rescued ' + firstPassCount + ' listener(s) held inside the CMP registry (consumers that registered before this file executed).');
            scheduleGptRedispatchIfSilent();
        }
        var elapsedMilliseconds = 0;
        var rescueTimer = setInterval(function () {
            if (!syntheticAnswersActive()) {
                clearInterval(rescueTimer);
                return;
            }
            var passCount = rescueCmpHeldListeners() + rescueGppRegistryListeners();
            if (passCount > 0) {
                logWatchdog('rescued ' + passCount + ' more CMP-held listener(s) on a retry pass.');
                scheduleGptRedispatchIfSilent();
            }
            elapsedMilliseconds = elapsedMilliseconds + CMP_RESCUE_RETRY_INTERVAL_MILLISECONDS;
            if (elapsedMilliseconds >= CMP_RESCUE_MAX_DURATION_MILLISECONDS) {
                clearInterval(rescueTimer);
            }
        }, CMP_RESCUE_RETRY_INTERVAL_MILLISECONDS);
    }

    // Marks the CMP as finished and stands the watchdog down. Called whenever a
    // real final event is observed through the proxy.
    function markCmpFinal() {
        if (watchdogState.cmpFinal) {
            return;
        }
        watchdogState.cmpFinal = true;
        stopProgressPoll();
        logWatchdog('CMP reached a final state on its own; watchdog stood down.');
    }

    function takeOver(reason) {
        if (watchdogState.takenOver || watchdogState.cmpFinal || watchdogState.cmpHealthy) {
            return;
        }
        stopProgressPoll();

        // Fail-safe: never bypass consent for a device that is clearly in the
        // EEA. For that visitor the watchdog does nothing at all.
        if (deviceLooksEea()) {
            watchdogState.timezoneBlocked = true;
            logWatchdog('CMP looks broken (' + reason + ') but the device timezone is ' + watchdogState.timezone +
                ' (EEA); leaving consent to the real CMP, no synthetic answer for this visitor.');
            return;
        }

        watchdogState.takenOver = true;
        watchdogState.takeoverReason = reason;
        logWatchdog(
            'taking over: ' + reason + '; answering ' + proxiedListeners.length +
            ' registered TCF listener(s) with gdprApplies=false so ad serving can proceed.');
        var listenerIndex;
        for (listenerIndex = 0; listenerIndex < proxiedListeners.length; listenerIndex++) {
            answerListenerSynthetically(proxiedListeners[listenerIndex]);
        }
        takeOverGpp();
        startCmpRegistryRescueLoop();
    }

    // Watches a real CMP event. Final states per the TCF spec are "tcloaded"
    // (no UI needed) and "useractioncomplete" (user answered the banner).
    // "cmpuishown" is not final but proves the CMP is configured and working
    // for this visitor, so it counts as the CMP asserting itself.
    function observeRealEvent(tcData) {
        // A synthetic answer produced by this watchdog (delivered back to us
        // through the CMP registry rescue) must never count as the CMP
        // recovering -- only genuine CMP events may stand the watchdog down.
        if (tcData && tcData.syntheticWatchdogAnswer) {
            return;
        }
        if (!tcData) {
            return;
        }
        if (tcData.eventStatus === 'tcloaded' || tcData.eventStatus === 'useractioncomplete') {
            markCmpHealthy('real final event ' + tcData.eventStatus);
            markCmpFinal();
            return;
        }
        if (tcData.eventStatus === 'cmpuishown' || tcData.cmpStatus === 'loaded') {
            markCmpHealthy('real event ' + tcData.eventStatus + ' / cmpStatus ' + tcData.cmpStatus);
        }
    }



    // Consumers inside iframes -- and, on some pages, GPT itself -- talk to
    // the CMP through the TCF postMessage protocol aimed at the
    // __tcfapiLocator frame instead of calling window.__tcfapi directly.
    // Those requests never pass through the function proxy above, so after a
    // takeover the watchdog answers them here as well. Verified live on sd.rs
    // (03.09.2026): GPT bound itself to the postMessage path and stayed
    // blocked even after the function-path rescue -- __tcfapiCall messages
    // flowed with ZERO __tcfapiReturn replies from the broken CMP. While the
    // CMP is healthy (or before a takeover) this responder stays silent, and
    // it stands down permanently if the real CMP asserts itself.
    function installPostMessageResponder() {
        window.addEventListener('message', function (messageEvent) {
            if (!syntheticAnswersActive()) {
                return;
            }
            var payload = messageEvent && messageEvent.data;
            var usedJsonEncoding = false;
            if (typeof payload === 'string') {
                if (payload.indexOf('__tcfapiCall') === -1) {
                    return;
                }
                try {
                    payload = JSON.parse(payload);
                    usedJsonEncoding = true;
                } catch (parseError) {
                    return;
                }
            }
            if (!payload || !payload.__tcfapiCall || !messageEvent.source) {
                return;
            }
            var tcfCall = payload.__tcfapiCall;
            var returnValue;
            if (tcfCall.command === 'ping') {
                returnValue = {
                    gdprApplies: false,
                    cmpLoaded: true,
                    cmpStatus: 'loaded',
                    displayStatus: 'disabled',
                    apiVersion: '2.2',
                    cmpId: 31
                };
            } else {
                returnValue = buildSyntheticTcData();
            }
            var reply = { __tcfapiReturn: { returnValue: returnValue, success: true, callId: tcfCall.callId } };
            try {
                messageEvent.source.postMessage(usedJsonEncoding ? JSON.stringify(reply) : reply, '*');
            } catch (postError) {
                return;
            }
            // A consumer reached through this path may also have had its ad
            // requests dropped while blocked; make sure the one-time recovery
            // refresh check runs for this path too.
            if (tcfCall.command === 'addEventListener' || tcfCall.command === 'getTCData') {
                scheduleGptRedispatchIfSilent();
            }
        }, false);
    }

    // =========================================================================
    // GPP (GLOBAL PRIVACY PLATFORM) WATCHDOG
    // =========================================================================
    //
    // The same broken CMP that blocks TCF also implements a second, independent
    // consent signal API: GPP (Global Privacy Platform, used for US state
    // privacy laws and other non-EU frameworks). It fails identically:
    // window.__gpp answers every query with cmpStatus "loading" and
    // signalStatus "not ready" forever. Google Publisher Tag consults GPP in
    // addition to TCF before it will request ads, so even after the TCF
    // watchdog above frees GPT, a stuck GPP signal can still block ad serving
    // on its own. Confirmed live on sd.rs on 03.09.2026: TCF was fully
    // resolved (registry rescue answered every tcfv2 listener, prebid ran a
    // complete auction), yet zero ad requests ever fired until GPP was also
    // freed here.
    //
    // Mirrors the TCF watchdog's three surfaces -- the window.__gpp function,
    // the consentmanager registry (entries with type 'gpp'), and the
    // postMessage protocol via the __gppLocator frame -- and fires on the same
    // takeover as TCF. A healthy GPP resolves on its own and this code never
    // engages.

    var gppRealApi = null;
    var gppProxiedListeners = [];
    var gppNextSyntheticListenerId = 300000;
    var gppRescuedRegistryIds = {};

    // A resolved-and-nothing-applies GPP signal: no section of any privacy
    // framework currently applies, which is the correct declaration when the
    // audience is not covered by the regulations GPP encodes (this wrapper's
    // watchdog already declares the TCF/GDPR equivalent above for the same
    // non-EEA audience).
    function buildSyntheticGppPingData() {
        return {
            gppVersion: '1.1',
            cmpStatus: 'loaded',
            cmpDisplayStatus: 'hidden',
            signalStatus: 'ready',
            supportedAPIs: [],
            cmpId: 31,
            sectionList: [],
            applicableSections: [],
            gppString: '',
            parsedSections: {}
        };
    }

    // GPP's addEventListener callback receives an "event" object, not the ping
    // data directly. Different integrations read either event.data (spec says
    // a plain 'ready' string for the signalStatus event) or the ping-shaped
    // fields nested under event.pingData -- both are populated here, same
    // belt-and-braces approach as the TCF synthetic answer above.
    function buildSyntheticGppEvent(listenerId) {
        return {
            eventName: 'signalStatus',
            listenerId: listenerId,
            data: 'ready',
            pingData: buildSyntheticGppPingData()
        };
    }

    function answerGppListenerSynthetically(listenerEntry) {
        try {
            listenerEntry.callback(buildSyntheticGppEvent(listenerEntry.syntheticListenerId), true);
        } catch (listenerError) {
            logWatchdog('a GPP listener threw while receiving the synthetic answer: ' + listenerError);
        }
    }

    function takeOverGpp() {
        var listenerIndex;
        for (listenerIndex = 0; listenerIndex < gppProxiedListeners.length; listenerIndex++) {
            answerGppListenerSynthetically(gppProxiedListeners[listenerIndex]);
        }
        rescueGppRegistryListeners();
    }

    // Same shape as the TCF registry rescue, filtered to type 'gpp' entries;
    // deduped by registry id so a repeat retry pass never answers twice.
    function rescueGppRegistryListeners() {
        var rescuedCount = 0;
        try {
            var cmpVendorObject = window.cmpmngr;
            if (!cmpVendorObject || !cmpVendorObject.api || !cmpVendorObject.api.events || typeof cmpVendorObject.api.events.forEach !== 'function') {
                return 0;
            }
            cmpVendorObject.api.events.forEach(function (registryEntry) {
                if (!registryEntry || registryEntry.type !== 'gpp' || typeof registryEntry.listener !== 'function') {
                    return;
                }
                if (gppRescuedRegistryIds[registryEntry.id] === true) {
                    return;
                }
                gppRescuedRegistryIds[registryEntry.id] = true;
                try {
                    registryEntry.listener(buildSyntheticGppEvent(registryEntry.id), true);
                    rescuedCount = rescuedCount + 1;
                } catch (listenerError) {
                    logWatchdog('a CMP-held GPP listener threw while receiving the synthetic answer: ' + listenerError);
                }
            });
        } catch (rescueError) {
            logWatchdog('GPP registry rescue skipped: ' + rescueError);
        }
        return rescuedCount;
    }

    function installGppProxy() {
        if (typeof window.__gpp !== 'function') {
            return;
        }
        gppRealApi = window.__gpp;

        window.__gpp = function proxiedGpp(command, callback, parameter) {

            if (command === 'addEventListener' && typeof callback === 'function') {
                var listenerEntry = {
                    callback: callback,
                    syntheticListenerId: gppNextSyntheticListenerId
                };
                gppNextSyntheticListenerId = gppNextSyntheticListenerId + 1;
                gppProxiedListeners.push(listenerEntry);

                if (syntheticAnswersActive()) {
                    answerGppListenerSynthetically(listenerEntry);
                }

                gppRealApi(command, function (event, success) {
                    if (syntheticAnswersActive()) {
                        return;
                    }
                    callback(event, success);
                }, parameter);
                return;
            }

            if (command === 'removeEventListener') {
                var entryIndex;
                for (entryIndex = 0; entryIndex < gppProxiedListeners.length; entryIndex++) {
                    if (gppProxiedListeners[entryIndex].syntheticListenerId === parameter) {
                        gppProxiedListeners.splice(entryIndex, 1);
                        if (typeof callback === 'function') {
                            callback(true);
                        }
                        return;
                    }
                }
                gppRealApi(command, callback, parameter);
                return;
            }

            if (syntheticAnswersActive()) {
                if (typeof callback === 'function') {
                    callback(buildSyntheticGppPingData(), true);
                    return;
                }
                return;
            }

            gppRealApi(command, callback, parameter);
        };
    }

    // Consumers reaching GPP through postMessage (__gppCall / __gppReturn via
    // the __gppLocator frame), same reasoning as the TCF postMessage responder
    // above: some consumers, including GPT itself on some page loads, never
    // call window.__gpp directly.
    function installGppPostMessageResponder() {
        window.addEventListener('message', function (messageEvent) {
            if (!syntheticAnswersActive()) {
                return;
            }
            var payload = messageEvent && messageEvent.data;
            var usedJsonEncoding = false;
            if (typeof payload === 'string') {
                if (payload.indexOf('__gppCall') === -1) {
                    return;
                }
                try {
                    payload = JSON.parse(payload);
                    usedJsonEncoding = true;
                } catch (parseError) {
                    return;
                }
            }
            if (!payload || !payload.__gppCall || !messageEvent.source) {
                return;
            }
            var gppCallInfo = payload.__gppCall;
            var returnValue = gppCallInfo.command === 'addEventListener'
                ? buildSyntheticGppEvent(gppCallInfo.callId)
                : buildSyntheticGppPingData();
            var reply = { __gppReturn: { returnValue: returnValue, success: true, callId: gppCallInfo.callId } };
            try {
                messageEvent.source.postMessage(usedJsonEncoding ? JSON.stringify(reply) : reply, '*');
            } catch (postError) {
                return;
            }
            scheduleGptRedispatchIfSilent();
        }, false);
    }


    function installProxy() {
        realTcfApi = window.__tcfapi;
        watchdogState.wrapped = true;
        installPostMessageResponder();
        installGppProxy();
        installGppPostMessageResponder();

        window.__tcfapi = function proxiedTcfApi(command, version, callback, parameter) {

            // --- addEventListener -------------------------------------------
            // Record the consumer so the watchdog can answer it on takeover,
            // and ALWAYS forward the registration to the real CMP so that a
            // healthy (or later-asserting) CMP drives the consumer normally.
            if (command === 'addEventListener' && typeof callback === 'function') {
                var listenerEntry = {
                    callback: callback,
                    syntheticListenerId: nextSyntheticListenerId
                };
                nextSyntheticListenerId = nextSyntheticListenerId + 1;
                proxiedListeners.push(listenerEntry);

                if (syntheticAnswersActive()) {
                    // Late consumer after takeover: answer immediately so it
                    // never hangs, and still register with the real CMP below
                    // in case it asserts itself.
                    answerListenerSynthetically(listenerEntry);
                }

                realTcfApi(command, version, function (tcData, success) {
                    observeRealEvent(tcData);
                    // While synthetic answers are in force, suppress the CMP's
                    // eternal "loading" pings; only real progress is worth
                    // forwarding then (and observeRealEvent just lifted the
                    // suppression if this event was real progress).
                    if (syntheticAnswersActive()) {
                        return;
                    }
                    callback(tcData, success);
                }, parameter);
                return;
            }

            // --- removeEventListener ----------------------------------------
            // If the id is one of the watchdog's synthetic ids, confirm the
            // removal locally (the real CMP has never heard of that id).
            if (command === 'removeEventListener') {
                var entryIndex;
                for (entryIndex = 0; entryIndex < proxiedListeners.length; entryIndex++) {
                    if (proxiedListeners[entryIndex].syntheticListenerId === parameter) {
                        proxiedListeners.splice(entryIndex, 1);
                        if (typeof callback === 'function') {
                            callback(true);
                        }
                        return;
                    }
                }
                realTcfApi(command, version, callback, parameter);
                return;
            }

            // --- getTCData / ping / everything else -------------------------
            // While synthetic answers are in force, answer one-shot queries
            // synthetically (the real CMP would only ever say "loading"
            // again). Otherwise pure pass-through, with a peek at the answer
            // to spot progress or a final state.
            if (syntheticAnswersActive()) {
                if (command === 'ping' && typeof callback === 'function') {
                    callback({
                        gdprApplies: false,
                        cmpLoaded: true,
                        cmpStatus: 'loaded',
                        displayStatus: 'disabled',
                        apiVersion: '2.2',
                        cmpId: 31
                    }, true);
                    return;
                }
                if (typeof callback === 'function') {
                    callback(buildSyntheticTcData(), true);
                    return;
                }
                return;
            }

            realTcfApi(command, version, function (tcData, success) {
                observeRealEvent(tcData);
                if (typeof callback === 'function') {
                    callback(tcData, success);
                }
            }, parameter);
        };

        // The watchdog's own observer: register with the real CMP so progress
        // or a final state is noticed even if no other consumer ever registers.
        realTcfApi('addEventListener', 2, function (tcData, success) {
            if (success !== false) {
                observeRealEvent(tcData);
            }
        });

        proxyInstalledAtMilliseconds = nowMilliseconds();
        progressPollTimer = setInterval(evaluateCmpProgress, CMP_WATCHDOG_POLL_MILLISECONDS);
        logWatchdog('proxy installed; watching CMP progress (takeover ' + CMP_NO_DESIGN_CONFIRM_MILLISECONDS +
            'ms after an empty configuration answer, or after ' + CMP_WATCHDOG_MAX_WAIT_MILLISECONDS + 'ms without any progress).');
    }

    // The CMP stub normally exists before this file runs; if not, poll for a
    // while in case the template loads it late. No CMP at all means nothing
    // blocks ad serving, so the watchdog simply never engages.
    if (typeof window.__tcfapi === 'function') {
        installProxy();
    } else {
        var appearanceWaitedMilliseconds = 0;
        var appearancePollTimer = setInterval(function () {
            if (typeof window.__tcfapi === 'function') {
                clearInterval(appearancePollTimer);
                installProxy();
                return;
            }
            appearanceWaitedMilliseconds = appearanceWaitedMilliseconds + CMP_APPEARANCE_POLL_MILLISECONDS;
            if (appearanceWaitedMilliseconds >= CMP_APPEARANCE_MAX_WAIT_MILLISECONDS) {
                clearInterval(appearancePollTimer);
                logWatchdog('no __tcfapi appeared within ' + CMP_APPEARANCE_MAX_WAIT_MILLISECONDS + 'ms; nothing to guard.');
            }
        }, CMP_APPEARANCE_POLL_MILLISECONDS);
    }
})();

// ID5 for secure signals sharing in GAM
window.ID5EspConfig = { 
    partnerId: 1355 // ID5 partnerId
};

// Amount of time (ms) that Prebid allows the bidders to respond to ad requests.
var PREBID_TIMEOUT = 1500;

// Initial Billboard auction timeout for A/B testing.
// Billboard is split from the rest of ATF so it can render faster without changing BTF/refresh auctions.
var BILLBOARD_PREBID_TIMEOUT = 1500;

// Number of seconds to wait after the slot becomes viewable.
var SECONDS_TO_WAIT_AFTER_VIEWABILITY = 30;

// Sticky/Adhesion refresh test.
// Only Sticky should refresh faster in Variant B; all other slots stay on the default 30s interval.
var STICKY_SECONDS_TO_WAIT_AFTER_VIEWABILITY = 20;

// Top Billboard baseline refresh policy.
// Ordinary demand waits for 10 seconds of continuous qualifying viewability.
// Billboard_2, Billboard_3, Billboard_4 and Billboard_5 keep the normal 30-second behavior.
var TOP_BILLBOARD_SLOT_ELEMENT_ID = 'Billboard';
var BILLBOARD_DEFAULT_REFRESH_SECONDS_TO_WAIT_AFTER_VIEWABILITY = 10;
var INITIAL_REFRESH_INTERVAL_TARGETING_VALUE = 'initial';
var INITIAL_REFRESH_POLICY_TARGETING_VALUE = 'initial';

// Advertiser-driven 30 second refresh override.
// Applies to every refreshing slot: whichever creative renders, if its advertiser
// is on this list, the slot's refresh wait becomes 30 seconds regardless of position,
// overriding Billboard's 10s, Adhesion's 20s and the P2/P3/LeftSide_Mrec 20s early tiers.
// Re-evaluated on every render, so a slot returns to its own default once a
// non-listed advertiser renders there.
var ADVERTISER_OVERRIDE_SECONDS_TO_WAIT_AFTER_VIEWABILITY = 30;
var ADVERTISER_OVERRIDE_REFRESH_POLICY_TARGETING_VALUE = 'advertiser_30_seconds';

// Prevent a missing GPT render event from leaving a Billboard refresh auction locked forever.
var BILLBOARD_RENDER_EVENT_WATCHDOG_MILLISECONDS = 10000;

// P2/P3/LeftSide_Mrec controlled 20s refresh test.
// For P2, P3 and LeftSide_Mrec only: refresh 1, 2 and 3 use 20s; refresh 4+ returns to the normal 30s cadence.
var P2_P3_LEFTSIDE_EARLY_REFRESH_SECONDS_TO_WAIT_AFTER_VIEWABILITY = 20;
var P2_P3_LEFTSIDE_EARLY_REFRESH_LIMIT = 3;

// Advertisers for which refresh shouldn't apply. Just add the company IDs e.g [3219368852, 3130234283].
var GAM_NOREFRESH_ADVERTISER_IDS = [];

// Campaigns for which refresh shouldn't apply. Just add the order IDs e.g [3219368852, 3130234283].
var GAM_NOREFRESH_ORDER_IDS = [3639261750, 3771499661, 3892436832, 3925494229, 4102792221];

// Ad units (div ids) for which refresh shouldn't apply. Just add the div IDs e.g ['Branding_Left', 'Branding_Right'].
var GAM_NOREFRESH_ADUNITS = ['Branding_Left', 'Branding_Right'];

// Advertisers whose rendered creative must wait 30 seconds before refreshing,
// on any ad unit (see ADVERTISER_OVERRIDE_SECONDS_TO_WAIT_AFTER_VIEWABILITY above).
var GAM_30_SECOND_ADVERTISER_IDS = [
    5081634183,
    5081526668
];

function getNormalizedGAMIdentifier(identifier) {
    if (identifier === null || typeof identifier === 'undefined' || identifier === '') {
        return null;
    }

    var normalizedIdentifier = Number(identifier);

    if (!isFinite(normalizedIdentifier)) {
        return null;
    }

    return normalizedIdentifier;
}

// Position/refresh-count based default, before any advertiser override is applied.
function getDefaultRefreshSecondsToWaitAfterViewability(slotElementId, refreshCount) {
    var completedRefreshCount = parseInt(refreshCount, 10);

    if (isNaN(completedRefreshCount)) {
        completedRefreshCount = 0;
    }

    if (slotElementId === 'Adhesion') {
        return STICKY_SECONDS_TO_WAIT_AFTER_VIEWABILITY;
    }

    if (slotElementId === TOP_BILLBOARD_SLOT_ELEMENT_ID) {
        return BILLBOARD_DEFAULT_REFRESH_SECONDS_TO_WAIT_AFTER_VIEWABILITY;
    }

    if (
        (
            slotElementId === 'P2' ||
            slotElementId === 'P3' ||
            slotElementId === 'LeftSide_Mrec'
        ) &&
        completedRefreshCount < P2_P3_LEFTSIDE_EARLY_REFRESH_LIMIT
    ) {
        return P2_P3_LEFTSIDE_EARLY_REFRESH_SECONDS_TO_WAIT_AFTER_VIEWABILITY;
    }

    return SECONDS_TO_WAIT_AFTER_VIEWABILITY;
}

// Refresh policy for any slot: its position/refresh-count default, unless the
// rendered creative's advertiser is on the 30 second override list, in which
// case the wait becomes (or stays) 30 seconds regardless of position.
function getRefreshPolicy(slotElementId, refreshCount, advertiserId) {
    var defaultDelaySeconds = getDefaultRefreshSecondsToWaitAfterViewability(slotElementId, refreshCount);
    var normalizedAdvertiserId = getNormalizedGAMIdentifier(advertiserId);
    var advertiserUsesThirtySecondOverride = false;

    if (normalizedAdvertiserId !== null) {
        advertiserUsesThirtySecondOverride = GAM_30_SECOND_ADVERTISER_IDS.includes(normalizedAdvertiserId);
    }

    if (advertiserUsesThirtySecondOverride) {
        return {
            refreshDelaySeconds: ADVERTISER_OVERRIDE_SECONDS_TO_WAIT_AFTER_VIEWABILITY,
            refreshIntervalTargetingValue: String(ADVERTISER_OVERRIDE_SECONDS_TO_WAIT_AFTER_VIEWABILITY),
            refreshPolicyTargetingValue: ADVERTISER_OVERRIDE_REFRESH_POLICY_TARGETING_VALUE
        };
    }

    return {
        refreshDelaySeconds: defaultDelaySeconds,
        refreshIntervalTargetingValue: String(defaultDelaySeconds),
        refreshPolicyTargetingValue: 'default_' + defaultDelaySeconds + '_seconds'
    };
}

// How far should the ad be for the lazy loading to activate.
var DISTANCE_IN_PIXELS_FOR_LAZY_LOADING = "350px";

// How much (in percent) should the ad slot be viewable. 
var VISIBILITY_PERCENT_FOR_REFRESH = 0.7;

// Bucketed refresh reporting for GAM.
// Values must match the predefined GAM key-values:
// initial, r1_3, r4_plus
function getRefreshBucket(refreshCount) {
    var count = parseInt(refreshCount, 10);

    if (isNaN(count)) {
        count = 0;
    }

    if (count === 0) {
        return 'initial';
    }

    if (count >= 1 && count <= 3) {
        return 'r1_3';
    }

    return 'r4_plus';
}

// Optional refresh diagnostics.
// Enable per page with ?refresh_debug=1 or at runtime with window.BLIC_REFRESH_DEBUG = true.
window.BLIC_REFRESH_DEBUG = window.BLIC_REFRESH_DEBUG || /(?:\?|&)refresh_debug=1(?:&|$)/.test(window.location.search);

function blicRefreshDebug(action, details) {
    if (!window.BLIC_REFRESH_DEBUG || !window.console) {
        return;
    }

    var consoleMethod = 'log';
    var debugDetails = details;

    if (action === 'BLOCKED' || action === 'ERROR') {
        consoleMethod = 'warn';
    }

    if (!debugDetails) {
        debugDetails = {};
    }

    console[consoleMethod]('[BLIC REFRESH] ' + action, debugDetails);
}

var blicRefreshDebugListenerInstalled = false;

function installBlicRefreshDebugListener() {
    if (blicRefreshDebugListenerInstalled) {
        return;
    }

    blicRefreshDebugListenerInstalled = true;

    googletag.pubads().addEventListener('slotRenderEnded', function(event) {
        if (!window.BLIC_REFRESH_DEBUG) {
            return;
        }

        var slot = event.slot;
        var slotElementId = slot.getSlotElementId();
        var orderId = getNormalizedGAMIdentifier(event.campaignId);
        var advertiserId = getNormalizedGAMIdentifier(event.advertiserId);

        var blockedByOrder = GAM_NOREFRESH_ORDER_IDS.includes(orderId);
        var blockedByAdvertiser = GAM_NOREFRESH_ADVERTISER_IDS.includes(advertiserId);
        var blockedByAdUnit = GAM_NOREFRESH_ADUNITS.includes(slotElementId);
        var blockedByConfig = blockedByOrder || blockedByAdvertiser || blockedByAdUnit;
        var refreshStatus = 'ELIGIBLE BY CONFIG - still requires viewability, visible tab and no running auction';
        var debugAction = 'RENDERED';

        var nextRefreshPolicy = getRefreshPolicy(
            slotElementId,
            slot.getTargeting('refresh_count')[0],
            advertiserId
        );

        if (blockedByConfig) {
            refreshStatus = 'WILL NOT REFRESH - excluded by wrapper configuration';
            debugAction = 'BLOCKED';
        }

        var details = {
            slot: slotElementId,
            orderId: event.campaignId,
            lineItemId: event.lineItemId,
            creativeId: event.creativeId,
            creativeTemplateId: event.creativeTemplateId,
            advertiserId: event.advertiserId,
            isBackfill: event.isBackfill,
            isEmpty: event.isEmpty,
            size: event.size,
            refreshCount: slot.getTargeting('refresh_count')[0],
            refreshBucket: slot.getTargeting('refresh_bucket')[0],
            blockedByOrder: blockedByOrder,
            blockedByAdvertiser: blockedByAdvertiser,
            blockedByAdUnit: blockedByAdUnit,
            nextRefreshDelaySeconds: nextRefreshPolicy.refreshDelaySeconds,
            nextRefreshPolicy: nextRefreshPolicy.refreshPolicyTargetingValue,
            refreshStatus: refreshStatus,
            // The aq_* values that were attached to the request which produced
            // this render, read back from the slot for verification.
            auctionQuality: readAuctionQualityTargeting(slot)
        };

        blicRefreshDebug(debugAction, details);
    });
}

// ---------------------------------------------------------------------------
// PASSIVE AUCTION-QUALITY (aq_*) TELEMETRY
// ---------------------------------------------------------------------------
// Ported from the Zena.rs wrapper (production build 20072026) on 27.07.2026.
//
// This instrumentation is reporting-only. It records how much competition the
// just-completed Prebid auction produced for a slot, and what that slot
// rendered on its previous request. It must never delay, cancel, reorder or
// otherwise influence ad delivery: nothing in the wrapper is allowed to read
// these values to make a decision.
//
// The key names and the value sets below must stay identical to the Zena.rs
// implementation. The aq_* keys are registered once for the whole GAM network
// (161634325) and Blic reports are compared directly against Zena reports, so
// a new key, a renamed value or a shifted bucket boundary would silently
// invalidate both sides of the comparison.
//
//   aq_bidder_count      0 | 1 | 2 | 3 | 4 | 5plus
//   aq_bid_count         0 | 1 | 2 | 3 | 4 | 5plus
//   aq_timed_out         yes | no
//   aq_prev_fill         unknown | filled | unfilled
//   aq_prev_source       unknown | unfilled | prebid | gam
//   aq_top_bid_bucket    no_bid | lt_010 | 010_019 | 020_049 | 050_099 | 100_plus
//
// Note on GAM registration: aq_bid_count is a reporting-only key (filterable
// but not a custom dimension) because GAM allows at most ten custom-dimension
// keys per network. aq_bidder_count carries almost the same signal and is the
// one to use as a report dimension.

// How many auction-quality records are kept per slot for in-page inspection.
var AUCTION_QUALITY_HISTORY_LIMIT = 50;

// How recent a Prebid bidWon event has to be for a render to be attributed to
// Prebid rather than to GAM's own demand.
var AUCTION_QUALITY_PREBID_WIN_MATCH_WINDOW_MILLISECONDS = 10000;

// The targeting keys written to GPT, listed once so capture/restore/read stay
// in sync with whatever applyAuctionQualityTargeting() sets.
var AUCTION_QUALITY_TARGETING_KEYS = [
    'aq_bidder_count',
    'aq_bid_count',
    'aq_timed_out',
    'aq_prev_fill',
    'aq_prev_source',
    'aq_top_bid_bucket'
];

// Per-slot telemetry state, keyed by div id.
// In this wrapper the div id is also the Prebid ad unit code and the GPT
// position value, so one key is enough to join all three.
var blicAuctionQualityStateBySlotElementId = {};

// The most recent Prebid win per slot. Used only to classify the render source
// of the creative that is currently on screen.
var blicAuctionQualityRecentPrebidWinsBySlotElementId = {};

function getOrCreateAuctionQualityState(slotElementId) {
    if (!blicAuctionQualityStateBySlotElementId.hasOwnProperty(slotElementId)) {
        blicAuctionQualityStateBySlotElementId[slotElementId] = {
            slotElementId: slotElementId,
            lastAuctionQuality: null,
            lastRenderResult: null,
            auctionQualityHistory: []
        };
    }

    return blicAuctionQualityStateBySlotElementId[slotElementId];
}

// Record Prebid wins so that a later render event can be attributed to either
// Prebid or GAM. This listener is purely observational.
pbjs.que.push(function() {
    if (window.blicAuctionQualityBidWonListenerInstalled) {
        return;
    }

    window.blicAuctionQualityBidWonListenerInstalled = true;

    if (typeof pbjs.onEvent !== 'function') {
        return;
    }

    pbjs.onEvent('bidWon', function(winningBid) {
        try {
            if (!winningBid) {
                return;
            }

            var slotElementId = winningBid.adUnitCode;

            if (!slotElementId) {
                return;
            }

            var winningBidderName = winningBid.bidder;

            if (!winningBidderName) {
                winningBidderName = winningBid.bidderCode;
            }

            if (!winningBidderName) {
                winningBidderName = null;
            }

            blicAuctionQualityRecentPrebidWinsBySlotElementId[slotElementId] = {
                timestamp: Date.now(),
                bidder: winningBidderName,
                cpm: Number(winningBid.cpm) || 0,
                adId: winningBid.adId || null,
                auctionId: winningBid.auctionId || null
            };
        }

        catch (error) {
            blicRefreshDebug('ERROR', {
                message: 'The auction-quality bidWon listener could not record a Prebid win.',
                error: error
            });
        }
    });
});

// Bidder and bid counts share one bucket set: 0, 1, 2, 3, 4 and 5plus.
function getAuctionQualityCountBucket(count) {
    var normalizedCount = Number(count);

    if (!isFinite(normalizedCount)) {
        normalizedCount = 0;
    }

    if (normalizedCount <= 0) {
        return '0';
    }

    if (normalizedCount === 1) {
        return '1';
    }

    if (normalizedCount === 2) {
        return '2';
    }

    if (normalizedCount === 3) {
        return '3';
    }

    if (normalizedCount === 4) {
        return '4';
    }

    return '5plus';
}

// Highest Prebid CPM of the auction, in EUR, bucketed for GAM reporting.
function getAuctionQualityTopBidBucket(highestBidCpm) {
    var normalizedCpm = Number(highestBidCpm);

    if (!isFinite(normalizedCpm)) {
        normalizedCpm = 0;
    }

    if (normalizedCpm <= 0) {
        return 'no_bid';
    }

    if (normalizedCpm < 0.10) {
        return 'lt_010';
    }

    if (normalizedCpm < 0.20) {
        return '010_019';
    }

    if (normalizedCpm < 0.50) {
        return '020_049';
    }

    if (normalizedCpm < 1.00) {
        return '050_099';
    }

    return '100_plus';
}

// Did the previous request on this slot return a creative?
// There is no previous render on a slot's very first request, which is
// reported as 'unknown'.
function getAuctionQualityPreviousFill(previousRenderResult) {
    if (!previousRenderResult) {
        return 'unknown';
    }

    if (previousRenderResult.isEmpty) {
        return 'unfilled';
    }

    return 'filled';
}

// Where did the creative of the previous request come from?
function getAuctionQualityPreviousSource(previousRenderResult) {
    if (!previousRenderResult) {
        return 'unknown';
    }

    if (previousRenderResult.isEmpty) {
        return 'unfilled';
    }

    if (previousRenderResult.source === 'prebid') {
        return 'prebid';
    }

    return 'gam';
}

// Find the GPT slot object for a div id among the slots GPT currently manages.
// Used where the wrapper only has the div id at hand.
function findGptSlotBySlotElementId(slotElementId) {
    try {
        var managedSlots = googletag.pubads().getSlots();

        for (var slotIndex = 0; slotIndex < managedSlots.length; slotIndex++) {
            var candidateSlot = managedSlots[slotIndex];

            if (
                candidateSlot &&
                candidateSlot.getSlotElementId &&
                candidateSlot.getSlotElementId() === slotElementId
            ) {
                return candidateSlot;
            }
        }
    }

    catch (error) {
        blicRefreshDebug('ERROR', {
            slot: slotElementId,
            message: 'Could not resolve the GPT slot for auction-quality reporting.',
            error: error
        });
    }

    return null;
}

// Build the auction-quality record for one slot out of the Prebid response set
// of the auction that has just finished for that slot.
//
// bidResponses is Prebid's bidsBackHandler payload, keyed by ad unit code. A
// slot that took part in the auction but received nothing simply has no key,
// which is a legitimate zero-competition result and is reported as such.
function collectAuctionQuality(
    slotElementId,
    gptSlot,
    auctionBidResponses,
    auctionTimedOut,
    auctionIdentifier,
    auctionRequestKind
) {
    var auctionQualityState = getOrCreateAuctionQualityState(slotElementId);
    var slotBids = [];

    try {
        var slotBidResponse = null;

        if (auctionBidResponses) {
            slotBidResponse = auctionBidResponses[slotElementId];
        }

        if (slotBidResponse && Array.isArray(slotBidResponse.bids)) {
            slotBids = slotBidResponse.bids.filter(function(bid) {
                return bid && Number(bid.cpm) >= 0;
            });
        }
    }

    catch (error) {
        slotBids = [];
    }

    var highestBid = null;

    slotBids.forEach(function(bid) {
        if (highestBid === null) {
            highestBid = bid;
            return;
        }

        if (Number(bid.cpm || 0) > Number(highestBid.cpm || 0)) {
            highestBid = bid;
        }
    });

    var bidderNames = [];

    slotBids.forEach(function(bid) {
        var bidderName = bid.bidder;

        if (!bidderName) {
            bidderName = bid.bidderCode;
        }

        if (bidderName && bidderNames.indexOf(bidderName) === -1) {
            bidderNames.push(bidderName);
        }
    });

    var highestBidCpm = 0;
    var highestBidderName = null;

    if (highestBid !== null) {
        highestBidCpm = Number(highestBid.cpm) || 0;
        highestBidderName = highestBid.bidder;

        if (!highestBidderName) {
            highestBidderName = highestBid.bidderCode;
        }

        if (!highestBidderName) {
            highestBidderName = null;
        }
    }

    // Diagnostic only, not part of any aq_* value: the refresh_count already
    // written to the slot for the request this record belongs to. Every call
    // site collects after refresh_count has been set, so this always matches
    // the refresh_count GAM receives on the same request and the two can be
    // joined when reading the debug log or the in-page snapshot.
    var refreshCountOfRequest = 0;

    try {
        if (gptSlot && gptSlot.getTargeting) {
            refreshCountOfRequest = parseInt(gptSlot.getTargeting('refresh_count')[0], 10);
        }
    }

    catch (error) {
        refreshCountOfRequest = 0;
    }

    if (isNaN(refreshCountOfRequest)) {
        refreshCountOfRequest = 0;
    }

    var auctionQuality = {
        timestamp: Date.now(),
        slotElementId: slotElementId,
        auctionId: auctionIdentifier || null,
        requestKind: auctionRequestKind || null,
        refreshCountOfRequest: refreshCountOfRequest,
        refreshBucketOfRequest: getRefreshBucket(refreshCountOfRequest),
        validBidCount: slotBids.length,
        bidderCount: bidderNames.length,
        bidderNames: bidderNames,
        highestBidCpm: highestBidCpm,
        highestBidder: highestBidderName,
        timedOut: Boolean(auctionTimedOut),
        previousRenderResult: auctionQualityState.lastRenderResult || null
    };

    auctionQualityState.lastAuctionQuality = auctionQuality;
    auctionQualityState.auctionQualityHistory.push(auctionQuality);

    if (auctionQualityState.auctionQualityHistory.length > AUCTION_QUALITY_HISTORY_LIMIT) {
        auctionQualityState.auctionQualityHistory.shift();
    }

    return auctionQuality;
}

// Did the auction hit the Prebid timeout?
function getAuctionQualityTimedOutValue(timedOut) {
    if (timedOut === true) {
        return 'yes';
    }

    return 'no';
}

// Translate an auction-quality record into the aq_* key-values GAM receives.
function getAuctionQualityTargetingValues(auctionQuality) {
    return {
        aq_bidder_count: getAuctionQualityCountBucket(auctionQuality.bidderCount),
        aq_bid_count: getAuctionQualityCountBucket(auctionQuality.validBidCount),
        aq_timed_out: getAuctionQualityTimedOutValue(auctionQuality.timedOut),
        aq_prev_fill: getAuctionQualityPreviousFill(auctionQuality.previousRenderResult),
        aq_prev_source: getAuctionQualityPreviousSource(auctionQuality.previousRenderResult),
        aq_top_bid_bucket: getAuctionQualityTopBidBucket(auctionQuality.highestBidCpm)
    };
}

// Read whatever aq_* values are currently set on a slot, so a failed request
// can be rolled back and so the debug listener can show what was actually sent.
function readAuctionQualityTargeting(gptSlot) {
    var currentValues = {};

    if (!gptSlot || !gptSlot.getTargeting) {
        return currentValues;
    }

    AUCTION_QUALITY_TARGETING_KEYS.forEach(function(targetingKey) {
        try {
            currentValues[targetingKey] = gptSlot.getTargeting(targetingKey)[0];
        }

        catch (error) {
            currentValues[targetingKey] = undefined;
        }
    });

    return currentValues;
}

// Restore previously captured aq_* values. Used only when a GPT refresh could
// not be sent, so the slot keeps describing the request it last really made.
function restoreAuctionQualityTargeting(gptSlot, capturedTargetingValues) {
    if (!gptSlot || !capturedTargetingValues) {
        return;
    }

    AUCTION_QUALITY_TARGETING_KEYS.forEach(function(targetingKey) {
        try {
            var capturedValue = capturedTargetingValues[targetingKey];

            if (typeof capturedValue === 'undefined') {
                gptSlot.clearTargeting(targetingKey);
                return;
            }

            gptSlot.setTargeting(targetingKey, capturedValue);
        }

        catch (error) {
            blicRefreshDebug('ERROR', {
                message: 'Could not restore an auction-quality key-value after a failed refresh.',
                key: targetingKey,
                error: error
            });
        }
    });
}

// Write the aq_* values onto a slot. GPT reads slot targeting at the moment
// refresh() is called, so this must run immediately before that call.
function applyAuctionQualityTargeting(gptSlot, auctionQuality) {
    if (!gptSlot || !auctionQuality) {
        return null;
    }

    var targetingValues = getAuctionQualityTargetingValues(auctionQuality);

    try {
        gptSlot.setTargeting('aq_bidder_count', targetingValues.aq_bidder_count);
        gptSlot.setTargeting('aq_bid_count', targetingValues.aq_bid_count);
        gptSlot.setTargeting('aq_timed_out', targetingValues.aq_timed_out);
        gptSlot.setTargeting('aq_prev_fill', targetingValues.aq_prev_fill);
        gptSlot.setTargeting('aq_prev_source', targetingValues.aq_prev_source);
        gptSlot.setTargeting('aq_top_bid_bucket', targetingValues.aq_top_bid_bucket);
    }

    catch (error) {
        blicRefreshDebug('ERROR', {
            slot: auctionQuality.slotElementId,
            message: 'The auction-quality key-values could not be set on the slot.',
            error: error
        });
        return null;
    }

    blicRefreshDebug('AUCTION QUALITY', {
        slot: auctionQuality.slotElementId,
        requestKind: auctionQuality.requestKind,
        refreshCountOfRequest: auctionQuality.refreshCountOfRequest,
        aq_bidder_count: targetingValues.aq_bidder_count,
        aq_bid_count: targetingValues.aq_bid_count,
        aq_timed_out: targetingValues.aq_timed_out,
        aq_prev_fill: targetingValues.aq_prev_fill,
        aq_prev_source: targetingValues.aq_prev_source,
        aq_top_bid_bucket: targetingValues.aq_top_bid_bucket,
        bidders: auctionQuality.bidderNames,
        topBidCpm: auctionQuality.highestBidCpm,
        topBidder: auctionQuality.highestBidder
    });

    return targetingValues;
}

// Collect and apply the aq_* values for a single slot in one step.
// Returns the applied values, or null when nothing was applied.
function collectAndApplyAuctionQuality(
    slotElementId,
    gptSlot,
    auctionBidResponses,
    auctionTimedOut,
    auctionIdentifier,
    auctionRequestKind
) {
    if (!gptSlot) {
        return null;
    }

    var auctionQuality = collectAuctionQuality(
        slotElementId,
        gptSlot,
        auctionBidResponses,
        auctionTimedOut,
        auctionIdentifier,
        auctionRequestKind
    );

    return applyAuctionQualityTargeting(gptSlot, auctionQuality);
}

// Apply the aq_* values to every slot of a batched initial GPT request.
//
// Only slots that actually took part in the Prebid auction are reported on.
// Branding_Left and Branding_Right ride along in the initial Billboard GPT
// request but have no Prebid ad unit, so giving them a zero-competition
// record would add meaningless rows to the reports. Zena does not report on
// its Branding slots either.
function applyAuctionQualityToBatchedSlots(
    gptSlots,
    auctionAdUnitCodes,
    auctionBidResponses,
    auctionTimedOut,
    auctionIdentifier,
    auctionRequestKind
) {
    if (!gptSlots || !gptSlots.length) {
        return;
    }

    gptSlots.forEach(function(gptSlot) {
        if (!gptSlot || !gptSlot.getSlotElementId) {
            return;
        }

        var slotElementId = gptSlot.getSlotElementId();

        if (!auctionAdUnitCodes || auctionAdUnitCodes.indexOf(slotElementId) === -1) {
            blicRefreshDebug('AUCTION QUALITY SKIPPED', {
                slot: slotElementId,
                requestKind: auctionRequestKind,
                reason: 'The slot has no Prebid ad unit in this auction.'
            });
            return;
        }

        collectAndApplyAuctionQuality(
            slotElementId,
            gptSlot,
            auctionBidResponses,
            auctionTimedOut,
            auctionIdentifier,
            auctionRequestKind
        );
    });
}

// Record what each slot rendered, so the next request on that slot can report
// its previous fill and previous source. Installed once, independently of the
// debug flag, because the telemetry has to work in production as well.
var blicAuctionQualityRenderListenerInstalled = false;

function installBlicAuctionQualityRenderListener() {
    if (blicAuctionQualityRenderListenerInstalled) {
        return;
    }

    blicAuctionQualityRenderListenerInstalled = true;

    googletag.pubads().addEventListener('slotRenderEnded', function(renderEvent) {
        try {
            if (!renderEvent || !renderEvent.slot || !renderEvent.slot.getSlotElementId) {
                return;
            }

            var slotElementId = renderEvent.slot.getSlotElementId();

            if (!slotElementId) {
                return;
            }

            var auctionQualityState = getOrCreateAuctionQualityState(slotElementId);
            var renderTimestamp = Date.now();
            var recentPrebidWin = blicAuctionQualityRecentPrebidWinsBySlotElementId[slotElementId];

            if (!recentPrebidWin) {
                recentPrebidWin = null;
            }

            var renderedSource = 'gam';

            if (renderEvent.isEmpty === true) {
                renderedSource = 'unfilled';
            }

            else if (
                recentPrebidWin !== null &&
                (renderTimestamp - recentPrebidWin.timestamp) <= AUCTION_QUALITY_PREBID_WIN_MATCH_WINDOW_MILLISECONDS
            ) {
                renderedSource = 'prebid';
            }

            var attributedPrebidWin = null;

            if (renderedSource === 'prebid') {
                attributedPrebidWin = recentPrebidWin;
            }

            auctionQualityState.lastRenderResult = {
                timestamp: renderTimestamp,
                isEmpty: Boolean(renderEvent.isEmpty),
                source: renderedSource,
                prebidWin: attributedPrebidWin,
                advertiserId: getNormalizedGAMIdentifier(renderEvent.advertiserId),
                orderId: getNormalizedGAMIdentifier(renderEvent.campaignId),
                lineItemId: getNormalizedGAMIdentifier(renderEvent.lineItemId),
                creativeId: getNormalizedGAMIdentifier(renderEvent.creativeId)
            };
        }

        catch (error) {
            blicRefreshDebug('ERROR', {
                message: 'The auction-quality render listener could not record a render result.',
                error: error
            });
        }
    });
}

// In-page inspection helper for manual verification.
window.__blicAuctionQualitySnapshot = function() {
    var snapshot = {};

    Object.keys(blicAuctionQualityStateBySlotElementId).forEach(function(slotElementId) {
        var auctionQualityState = blicAuctionQualityStateBySlotElementId[slotElementId];
        var gptSlot = findGptSlotBySlotElementId(slotElementId);

        snapshot[slotElementId] = {
            currentTargeting: readAuctionQualityTargeting(gptSlot),
            lastAuctionQuality: auctionQualityState.lastAuctionQuality || null,
            lastRenderResult: auctionQualityState.lastRenderResult || null,
            history: auctionQualityState.auctionQualityHistory.slice()
        };
    });

    try {
        return JSON.parse(JSON.stringify(snapshot));
    }

    catch (error) {
        return snapshot;
    }
};

// Variables for all defined slots.
var Adhesion;
var Billboard;
var Billboard_2;
var Billboard_3;
var Billboard_4;
var Billboard_5;
var LeftSide_Mrec;
var Branding_Left;
var Branding_Right;
var InFeed_1;
var InFeed_2;
var InFeed_3;
var InFeed_4;
var InFeed_5;
var InFeed_6;
var P1;
var P2;
var P3;
var P4;
var P5;
var InText_1;
var InText_2;
var InText_3;
var InText_4;
var InText_5;
var InText_6;
var InText_7;
var InText_8;
var InText_9;
var InText_10;
var Under_Article_1;

// Top level ad unit path. 
var adUnitPath = "/161634325/Blic.rs/";
let adWindowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth; //check user's screen width

// ATF and BTF array variables. 
var adUnitsATF = [];
var adUnitsBTF = [];

function runWrapper() {

    //console.log('Ad Wrapper: Script execution.');

    var adContainers = document.querySelectorAll('.wrapperAd'); // Find all divs which have wrapperAd class.

    googletag.cmd.push(function() { // Push the defined ad units to the GPT array.

        // Sticky GPT size mapping. 
        var sticky_mapping = googletag.sizeMapping()
        .addSize([1600, 0], [[728, 90], [970, 90], [980, 90], [1000, 100], [750, 100], [750, 200], [1, 1]]) //desktop for bigger screens
        .addSize([1024, 0], [[728, 90], [970, 90], [980, 90], [1000, 100], [750, 100], [1, 1]]) //desktop for smaller screens
        .addSize([469, 0], [[468, 60], [300, 50], [320, 50], [1, 1]]) //tablet
        .addSize([0, 0], [[300, 50], [320, 50], [300, 100], [320, 100], [1, 1]]) //mobile
        .build();

        // Branding GPT size mapping.
        var branding_left_mapping = googletag.sizeMapping()
        .addSize([1300, 0], [[2, 1], [470, 600], [470, 800], [470, 1050], [470, 1080]]) //desktop for smaller screens
        .addSize([0, 0], []) //tablet & mobile
        .build();

        // Branding GPT size mapping.
        var branding_right_mapping = googletag.sizeMapping()
        .addSize([1300, 0], [[2, 2], [470, 600], [470, 800], [470, 1050], [470, 1080]]) //desktop for smaller screens
        .addSize([0, 0], []) //tablet & mobile
        .build();

        // Billboard GPT size mapping.
        var billboard_mapping = googletag.sizeMapping()
        .addSize([1024, 0], [[728, 90], [970, 66], [950, 90], [970, 90], [980, 90], [980, 120], [1000, 100], [750, 100], [750, 200], [800, 250], [980, 120], [930, 180], [930, 240], [930, 250], [970, 250], [980, 240], [980, 250], [990, 250], [1060, 250], [1100, 250]]) //desktop
        .addSize([469, 0], [[468, 60], [300, 50], [320, 50]]) //tablet
        .addSize([0, 0], [[300, 50], [300, 75], [320, 80], [320, 50], [300, 100], [320, 100]]) //mobile
        .build();

        // Billboard 2 & 3 GPT size mapping
        var billboard2_3_mapping = googletag.sizeMapping()
            .addSize([1024, 0], [[728, 90], [920, 250], [920, 90], [750, 100], [750, 200], [800, 250], [930, 180], [930, 240], [930, 250]]) //desktop
            .addSize([469, 0], [[468, 60], [300, 50], [320, 50]]) //tablet
            .addSize([0, 0], [[300, 50], [300, 75], [320, 80], [320, 50], [300, 100], [320, 100]]) //mobile
            .build();

        // LeftSide_Mrec GPT size mapping
        var leftside_mrec_mapping = googletag.sizeMapping()
            .addSize([1024, 0], [[200, 200], [250, 250], [300, 250], [320, 250], 'fluid']) //desktop
            .addSize([469, 0], [[468, 60], [300, 50], [320, 50]]) //tablet
            .addSize([0, 0], [[300, 50], [300, 75], [320, 80], [320, 50], [300, 100], [320, 100]]) //mobile
            .build();
        
        // InFeed GPT size mapping.
        var inFeed_mapping = googletag.sizeMapping()
        .addSize([1024, 0], [[580, 280], [250, 250], [300, 250], [320, 250], [320, 240], [336, 280], 'fluid']) //desktop
        .addSize([469, 0], [[468, 60], [250, 250], [300, 250], [320, 250], [320, 240], [336, 280], 'fluid']) //tablet
        .addSize([0, 0], []) //mobile
        .build();

        // P1 & P2 GPT size mapping.
        var p1p2_mapping = googletag.sizeMapping()
        .addSize([1024, 0], [[200, 200], [250, 250], [300, 250], [120, 600], [160, 600], [300, 600], [300, 1050]]) //desktop
        .addSize([469, 0], [[200, 200], [250, 250], [300, 250]]) //tablet
        .addSize([0, 0], [[200, 200], [250, 250], [300, 250], [320, 250], [320, 240], [336, 280]]) //mobile
        .build();

        // P GPT size mapping.
        var p_mapping = googletag.sizeMapping()
        .addSize([1024, 0], [[200, 200], [250, 250], [300, 250], [120, 600], [160, 600], [300, 600]]) //desktop
        .addSize([469, 0], [[200, 200], [250, 250], [300, 250]]) //tablet
        .addSize([0, 0], [[200, 200], [250, 250], [300, 250], [320, 250], [320, 240], [336, 280]]) //mobile
        .build();

        // InText GPT size mapping.
        var inText_mapping = googletag.sizeMapping()
        .addSize([1024, 0], [[580, 280], [200, 200], [250, 250], [300, 250], [320, 250], [320, 240], [336, 280], 'fluid']) //desktop
        .addSize([469, 0], [[300, 50], [320, 50], [468, 60], [200, 200], [250, 250], [300, 250], [320, 250], [320, 240], [336, 280], 'fluid']) //tablet
        .addSize([0, 0], [[300, 50], [300, 75], [320, 80], [320, 50], [300, 100], [320, 100], [200, 200], [250, 250], [300, 250], [320, 250], [320, 240], [336, 280], 'fluid']) //mobile
        .build();

        // UnderArticle GPT size mapping.
        var underArticle_mapping = googletag.sizeMapping()
        .addSize([1024, 0], [[200, 200], [250, 250], [300, 250], [320, 250], [320, 240], [336, 280], [728, 90], [750, 100], [750, 200], [740, 300], [740, 500], 'fluid']) //desktop
        .addSize([469, 0], [[300, 50], [200, 200], [250, 250], [300, 250], [320, 250], [320, 240], [336, 280], 'fluid']) //tablet
        .addSize([0, 0], []) //mobile
        .build();

        adContainers.forEach(function(el) { // Runs through all of the wrapperAd divs.

            // **************** Sticky GPT and Prebid definition. *******************

            if (el.id === 'Adhesion') { // Check if Adhesion div is present in body. 

                // Sticky ad unit definition
                Adhesion = googletag.defineSlot(adUnitPath + 'Sticky', [[728, 90], [970, 90], [320, 50]], 'Adhesion')
                                    .defineSizeMapping(sticky_mapping)
                                    .setTargeting('position', 'Adhesion')
                                    .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                    .addService(googletag.pubads());
                
                // Push the defined Adhesion Prebid placement IDs to the ATF array.      
                adUnitsATF.push(
                    {   code: 'Adhesion',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1600, 0], sizes: [[728, 90], [970, 90], [750, 100], [750, 200]] }, //desktop for bigger screens
                            { minViewPort: [1024, 0], sizes: [[728, 90], [970, 90], [750, 100]] }, //desktop for smaller screens
                            { minViewPort: [469, 0], sizes: [[468, 60], [300, 50], [320, 50], [300, 100], [320, 100]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-foot-blic-ringi-0rkxwpioiqd3yk"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890449', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2225875', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984421', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094755'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706094'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851804'}},
                            {bidder: "richaudience", params: {pid: "PPWiRAqpiT", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335784, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '120620'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_Adhesion_970x90'}},
                        ]
                    }
                );

            }

            // **************** Billboard GPT and Prebid definition. *******************

            else if (el.id === 'Billboard') { // Check if Billboard div is present in body. 

                // Billboard ad unit definition
                Billboard = googletag.defineSlot(adUnitPath + 'Billboard', [[930, 240], [930, 250], [970, 250]], 'Billboard')
                                     .defineSizeMapping(billboard_mapping)
                                     .setTargeting('position', 'Billboard')
                                     .setTargeting('refresh_count', '0')
                                     .setTargeting('refresh_bucket', 'initial')
                                     .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                     .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());
                
                // Push the defined Billboard Prebid placement IDs to the ATF array.      
                adUnitsATF.push(
                    {   code: 'Billboard',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[970, 90], [728, 90], [930, 240], [930, 250], [970, 250]] }, //desktop
                            // The 469-1023px band used to offer bidders only 468x60, a
                            // format almost nobody buys any more, so nearly every auction
                            // in this band was dead on arrival. It now offers the same set
                            // the GPT size mapping already allows for this band, so any
                            // Prebid win here is a size GAM can actually render. 728x90 is
                            // deliberately NOT added: GPT would refuse to render it in this
                            // band, so a Prebid win at that size would be a lost impression.
                            // A/B-proven on the top Billboard 27.08-01.09.2026 (zero-bid
                            // 69.7% -> 45.2%, Prebid impressions +31%) and applied to all
                            // Billboard slots, which share the same band defect.
                            { minViewPort: [469, 0], sizes: [[468, 60], [300, 50], [320, 50]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-kbixs32ulwqhsp"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890450', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2262441', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984422', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094756'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706095'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851805'}},
                            {bidder: "richaudience", params: {pid: "KtlS0lSKuL", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335786, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '120621'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_Billboard_970x250'}},
                        ]
                    }
                );

            }

            // **************** Billboard_2 GPT and Prebid definition. *******************

            else if (el.id === 'Billboard_2') { // Check if Billboard_2 div is present in body.

                // Billboard_2 ad unit definition
                Billboard_2 = googletag.defineSlot(adUnitPath + 'Billboard_2', [[930, 240], [930, 250]], 'Billboard_2')
                                     .defineSizeMapping(billboard2_3_mapping)
                                     .setTargeting('position', 'Billboard_2')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());

                // Push the defined Billboard_2 Prebid placement IDs to the BTF array.
                adUnitsBTF.push(
                    {   code: 'Billboard_2',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[728, 90], [930, 240], [930, 250]] }, //desktop
                            // The 469-1023px band used to offer bidders only 468x60, a
                            // format almost nobody buys any more, so nearly every auction
                            // in this band was dead on arrival. It now offers the same set
                            // the GPT size mapping already allows for this band, so any
                            // Prebid win here is a size GAM can actually render. 728x90 is
                            // deliberately NOT added: GPT would refuse to render it in this
                            // band, so a Prebid win at that size would be a lost impression.
                            // A/B-proven on the top Billboard 27.08-01.09.2026 (zero-bid
                            // 69.7% -> 45.2%, Prebid impressions +31%) and applied to all
                            // Billboard slots, which share the same band defect.
                            { minViewPort: [469, 0], sizes: [[468, 60], [300, 50], [320, 50]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-kbixs32ulwqhsp"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890450', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2262441', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984422', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094756'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706095'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851805'}},
                            {bidder: "richaudience", params: {pid: "KtlS0lSKuL", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335786, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '120621'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_Billboard_2_728x90'}},
                        ]
                    }
                );
            }

            // **************** Billboard_3 GPT and Prebid definition. *******************

            else if (el.id === 'Billboard_3') { // Check if Billboard_3 div is present in body.

                // Billboard_3 ad unit definition
                Billboard_3 = googletag.defineSlot(adUnitPath + 'Billboard_3', [[930, 240], [930, 250]], 'Billboard_3')
                                     .defineSizeMapping(billboard2_3_mapping)
                                     .setTargeting('position', 'Billboard_3')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());

                // Push the defined Billboard_3 Prebid placement IDs to the BTF array.
                adUnitsBTF.push(
                    {   code: 'Billboard_3',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[728, 90], [930, 240], [930, 250]] }, //desktop
                            // The 469-1023px band used to offer bidders only 468x60, a
                            // format almost nobody buys any more, so nearly every auction
                            // in this band was dead on arrival. It now offers the same set
                            // the GPT size mapping already allows for this band, so any
                            // Prebid win here is a size GAM can actually render. 728x90 is
                            // deliberately NOT added: GPT would refuse to render it in this
                            // band, so a Prebid win at that size would be a lost impression.
                            // A/B-proven on the top Billboard 27.08-01.09.2026 (zero-bid
                            // 69.7% -> 45.2%, Prebid impressions +31%) and applied to all
                            // Billboard slots, which share the same band defect.
                            { minViewPort: [469, 0], sizes: [[468, 60], [300, 50], [320, 50]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-kbixs32ulwqhsp"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890450', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2262441', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984422', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094756'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706095'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851805'}},
                            {bidder: "richaudience", params: {pid: "KtlS0lSKuL", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335786, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '120621'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_Billboard_3_728x90'}},
                        ]
                    }
                );
            }

            // **************** Billboard_4 GPT and Prebid definition. *******************

            else if (el.id === 'Billboard_4') { // Check if Billboard_4 div is present in body.

                // Billboard_4 ad unit definition
                Billboard_4 = googletag.defineSlot(adUnitPath + 'Billboard_4', [[930, 240], [930, 250]], 'Billboard_4')
                                     .defineSizeMapping(billboard2_3_mapping)
                                     .setTargeting('position', 'Billboard_4')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());

                // Push the defined Billboard_4 Prebid placement IDs to the BTF array.
                adUnitsBTF.push(
                    {   code: 'Billboard_4',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[728, 90], [930, 240], [930, 250]] }, //desktop
                            // The 469-1023px band used to offer bidders only 468x60, a
                            // format almost nobody buys any more, so nearly every auction
                            // in this band was dead on arrival. It now offers the same set
                            // the GPT size mapping already allows for this band, so any
                            // Prebid win here is a size GAM can actually render. 728x90 is
                            // deliberately NOT added: GPT would refuse to render it in this
                            // band, so a Prebid win at that size would be a lost impression.
                            // A/B-proven on the top Billboard 27.08-01.09.2026 (zero-bid
                            // 69.7% -> 45.2%, Prebid impressions +31%) and applied to all
                            // Billboard slots, which share the same band defect.
                            { minViewPort: [469, 0], sizes: [[468, 60], [300, 50], [320, 50]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-kbixs32ulwqhsp"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890450', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2262441', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984422', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094756'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706095'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851805'}},
                            {bidder: "richaudience", params: {pid: "KtlS0lSKuL", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335786, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '120621'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_Billboard_4_728x90'}},
                        ]
                    }
                );
            }

            // **************** Billboard_5 GPT and Prebid definition. *******************

            else if (el.id === 'Billboard_5') { // Check if Billboard_5 div is present in body.

                // Billboard_5 ad unit definition
                Billboard_5 = googletag.defineSlot(adUnitPath + 'Billboard_5', [[930, 240], [930, 250]], 'Billboard_5')
                                     .defineSizeMapping(billboard2_3_mapping)
                                     .setTargeting('position', 'Billboard_5')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());

                // Push the defined Billboard_5 Prebid placement IDs to the BTF array.
                adUnitsBTF.push(
                    {   code: 'Billboard_5',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[728, 90], [930, 240], [930, 250]] }, //desktop
                            // The 469-1023px band used to offer bidders only 468x60, a
                            // format almost nobody buys any more, so nearly every auction
                            // in this band was dead on arrival. It now offers the same set
                            // the GPT size mapping already allows for this band, so any
                            // Prebid win here is a size GAM can actually render. 728x90 is
                            // deliberately NOT added: GPT would refuse to render it in this
                            // band, so a Prebid win at that size would be a lost impression.
                            // A/B-proven on the top Billboard 27.08-01.09.2026 (zero-bid
                            // 69.7% -> 45.2%, Prebid impressions +31%) and applied to all
                            // Billboard slots, which share the same band defect.
                            { minViewPort: [469, 0], sizes: [[468, 60], [300, 50], [320, 50]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-kbixs32ulwqhsp"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890450', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2262441', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984422', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094756'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706095'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851805'}},
                            {bidder: "richaudience", params: {pid: "KtlS0lSKuL", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335786, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '120621'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_Billboard_5_728x90'}},
                        ]
                    }
                );
            }

            // **************** LeftSide_Mrec GPT and Prebid definition. *******************

            else if (el.id === 'LeftSide_Mrec') { // Check if LeftSide_Mrec div is present in body.

                // LeftSide_Mrec ad unit definition
                LeftSide_Mrec = googletag.defineSlot(adUnitPath + 'LeftSide_Mrec', [[300, 250], 'fluid'], 'LeftSide_Mrec')
                              .defineSizeMapping(leftside_mrec_mapping)
                              .setTargeting('position', 'LeftSide_Mrec')
                              .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                              .addService(googletag.pubads());

                // Push the defined LeftSide_Mrec Prebid placement IDs to the BTF array.
                adUnitsBTF.push(
                    {   code: 'LeftSide_Mrec',
                        mediaTypes: {banner: {
                        // Tablet and mobile sizes aligned to leftside_mrec_mapping on
                        // 05.09.2026: the GPT slot accepts only small banners below
                        // 1024 px, while Prebid was asking for rectangles that GPT
                        // could never render there. The slot is desktop-only on the
                        // live templates today, so this is a latent defect closed for
                        // the day the template changes.
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[300, 250]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[468, 60], [300, 50], [320, 50]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [300, 75], [320, 80], [320, 50], [300, 100], [320, 100]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: 'adf', params: {mid: '2265070', priceType: 'net'}},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-rktd7eu2pxz6mx"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1336690'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '565408418'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '7613175'}},
                            {bidder: "richaudience", params: {pid: "uiAkQTwbS3", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 4088021, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '154028'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_LeftSide_Mrec_300x250'}},
                        ]
                    }
                );

            }

            // **************** Branding_Left GPT and Prebid definition. *******************

            else if (el.id === 'Branding_Left') { // Check if Branding_Left div is present in body. 

                // Branding_Left ad unit definition
                Branding_Left = googletag.defineSlot(adUnitPath + 'Branding_Left', [[120, 600], [160, 600], [300, 600], [470, 1080]], 'Branding_Left')
                                            .defineSizeMapping(branding_left_mapping)
                                            .setTargeting('position', 'Branding_Left')
                                            .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                            .addService(googletag.pubads());
                

            }

            // **************** Branding_Right GPT and Prebid definition. *******************

            else if (el.id === 'Branding_Right') { // Check if Branding_Right div is present in body. 

                // Branding_Right ad unit definition
                Branding_Right = googletag.defineSlot(adUnitPath + 'Branding_Right', [[120, 600], [160, 600], [300, 600], [470, 1080]], 'Branding_Right')
                                            .defineSizeMapping(branding_right_mapping)
                                            .setTargeting('position', 'Branding_Right')
                                            .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                            .addService(googletag.pubads());
                

            }

            // **************** P1 GPT and Prebid definition. *******************

            else if (el.id === 'P1') { // Check if P1 div is present in body. 

                // P1 ad unit definition
                P1 = googletag.defineSlot(adUnitPath + 'P1', [[300, 250]], 'P1')
                              .defineSizeMapping(p1p2_mapping)
                              .setTargeting('position', 'P1')
                              .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                              .addService(googletag.pubads());
                
                // Push the defined P1 Prebid placement IDs to the ATF array.      
                adUnitsATF.push(
                    {   code: 'P1',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [120, 600], [160, 600], [300, 600]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[200, 200], [250, 250], [300, 250]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-3saextmmrw0hyf"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890453', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265040', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984423', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094759'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706098'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851808'}},
                            {bidder: "richaudience", params: {pid: "DVP8oGhUul", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335792, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '120624'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_P1_300x250'}},
                        ]
                    }
                );

            }

            // **************** P2 GPT and Prebid definition. *******************

            else if (el.id === 'P2') { // Check if P2 div is present in body. 

                // P2 ad unit definition
                P2 = googletag.defineSlot(adUnitPath + 'P2', [[300, 250]], 'P2')
                              .defineSizeMapping(p1p2_mapping)
                              .setTargeting('position', 'P2')
                              .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                              .addService(googletag.pubads());
                
                // Push the defined P2 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'P2',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [120, 600], [160, 600], [300, 600]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[200, 200], [250, 250], [300, 250]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-3saextmmrw0hyf"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890454', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265041', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984423', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094760'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706099'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851809'}},
                            {bidder: "richaudience", params: {pid: "kQSz4LE219", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335794, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '120625'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_P2_300x250'}},
                        ]
                    }
                );

            }

            // **************** P3 GPT and Prebid definition. *******************

            else if (el.id === 'P3') { // Check if P3 div is present in body. 

                // P3 ad unit definition
                P3 = googletag.defineSlot(adUnitPath + 'P3', [[300, 250]], 'P3')
                              .defineSizeMapping(p_mapping)
                              .setTargeting('position', 'P3')
                              .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                              .addService(googletag.pubads());
                
                // Push the defined P3 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'P3',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [120, 600], [160, 600], [300, 600]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[200, 200], [250, 250], [300, 250]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-3saextmmrw0hyf"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890455', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265042', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984423', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094761'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706100'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851810'}},
                            {bidder: "richaudience", params: {pid: "XH9YXTPsqC", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335796, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '120626'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_P3_300x250'}},
                        ]
                    }
                );

            }   

            
            // **************** P4 GPT and Prebid definition. *******************

            else if (el.id === 'P4') { // Check if P4 div is present in body. 

                // P4 ad unit definition
                P4 = googletag.defineSlot(adUnitPath + 'P4', [[300, 250]], 'P4')
                              .defineSizeMapping(p_mapping)
                              .setTargeting('position', 'P4')
                              .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                              .addService(googletag.pubads());
                
                // Push the defined P4 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'P4',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [120, 600], [160, 600], [300, 600]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[200, 200], [250, 250], [300, 250]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-3saextmmrw0hyf"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890456', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265043', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094762'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706101'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851811'}},
                            {bidder: "richaudience", params: {pid: "tohRGnMVmL", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335798, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '123584'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_P4_300x250'}},
                        ]
                    }
                );

            }   

            // **************** P5 GPT and Prebid definition. *******************

            else if (el.id === 'P5') { // Check if P5 div is present in body. 

                // P5 ad unit definition
                P5 = googletag.defineSlot(adUnitPath + 'P5', [[300, 250]], 'P5')
                              .defineSizeMapping(p_mapping)
                              .setTargeting('position', 'P5')
                              .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                              .addService(googletag.pubads());
                
                // Push the defined P5 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'P5',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [120, 600], [160, 600], [300, 600]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[200, 200], [250, 250], [300, 250]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-3saextmmrw0hyf"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890457', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265044', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094764'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706102'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851812'}},
                            {bidder: "richaudience", params: {pid: "GJDldTw13e", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335800, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '125177'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_P5_300x250'}},
                        ]
                    }
                );

            }   


            // **************** InFeed_1 GPT and Prebid definition. *******************

            else if (el.id === 'InFeed_1') { // Check if InFeed_1 div is present in body. 

                // InFeed_1 ad unit definition
                InFeed_1 = googletag.defineSlot(adUnitPath + 'InFeed_1', [[300, 250]], 'InFeed_1')
                                        .defineSizeMapping(inFeed_mapping)
                                        .setTargeting('position', 'InFeed_1')
                                        .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                        .addService(googletag.pubads());
                
                // Push the defined InFeed_1 Prebid placement IDs to the ATF array.      
                adUnitsATF.push(
                    {   code: 'InFeed_1',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[580, 280], [300, 250], [336, 280]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[300, 250], [336, 280]] }, //tablet
                            { minViewPort: [0, 0], sizes: [] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-2wrqmhjbwreqgk"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890470', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265052', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984449', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094774'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706110'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851826'}},
                            {bidder: "richaudience", params: {pid: "MbvdVxpF5N", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335816, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '125167'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InFeed_1_300x250'}},
                        ]
                    }
                );

            }

            // **************** InFeed_2 GPT and Prebid definition. *******************

            else if (el.id === 'InFeed_2') { // Check if InFeed_2 div is present in body. 

                // InFeed_2 ad unit definition
                InFeed_2 = googletag.defineSlot(adUnitPath + 'InFeed_2', [[300, 250]], 'InFeed_2')
                                        .defineSizeMapping(inFeed_mapping)
                                        .setTargeting('position', 'InFeed_2')
                                        .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                        .addService(googletag.pubads());
                
                // Push the defined InFeed_2 Prebid placement IDs to the ATF array.      
                // InFeed_2 is lazy-loaded (class lazyAd in the template), so it
                // belongs to the below-the-fold array. Until 05.09.2026 it sat in
                // adUnitsATF: the initial auction bid it for nothing (no GPT request
                // for it there) and the lazy observer auctioned it a second time.
                adUnitsBTF.push(
                    {   code: 'InFeed_2',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[580, 280], [300, 250], [336, 280]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[300, 250], [336, 280]] }, //tablet
                            { minViewPort: [0, 0], sizes: [] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-2wrqmhjbwreqgk"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890471', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265053', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984449', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094775'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706111'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851827'}},
                            {bidder: "richaudience", params: {pid: "IRb3aq7W6Z", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335818, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '125168'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InFeed_2_300x250'}},
                        ]
                    }
                );

            }

            // **************** InFeed_3 GPT and Prebid definition. *******************

            else if (el.id === 'InFeed_3') { // Check if InFeed_3 div is present in body. 

                // InFeed_3 ad unit definition
                InFeed_3 = googletag.defineSlot(adUnitPath + 'InFeed_3', [[300, 250]], 'InFeed_3')
                                        .defineSizeMapping(inFeed_mapping)
                                        .setTargeting('position', 'InFeed_3')
                                        .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                        .addService(googletag.pubads());
                
                // Push the defined InFeed_3 Prebid placement IDs to the ATF array.      
                // Lazy-loaded: below-the-fold array (see InFeed_2 for why).
                adUnitsBTF.push(
                    {   code: 'InFeed_3',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[580, 280], [300, 250], [336, 280]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[300, 250], [336, 280]] }, //tablet
                            { minViewPort: [0, 0], sizes: [] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-2wrqmhjbwreqgk"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890472', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265055', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984449', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094776'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706112'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851828'}},
                            {bidder: "richaudience", params: {pid: "fxR5wGFUFF", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335822, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '125169'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InFeed_3_300x250'}},
                        ]
                    }
                );

            }

            // **************** InFeed_4 GPT and Prebid definition. *******************

            else if (el.id === 'InFeed_4') { // Check if InFeed_4 div is present in body. 

                // InFeed_4 ad unit definition
                InFeed_4 = googletag.defineSlot(adUnitPath + 'InFeed_4', [[300, 250]], 'InFeed_4')
                                        .defineSizeMapping(inFeed_mapping)
                                        .setTargeting('position', 'InFeed_4')
                                        .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                        .addService(googletag.pubads());
                
                // Push the defined InFeed_4 Prebid placement IDs to the ATF array.      
                // Lazy-loaded: below-the-fold array (see InFeed_2 for why).
                adUnitsBTF.push(
                    {   code: 'InFeed_4',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[580, 280], [300, 250], [336, 280]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[300, 250], [336, 280]] }, //tablet
                            { minViewPort: [0, 0], sizes: [] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-2wrqmhjbwreqgk"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890473', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265056', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094777'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706113'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851829'}},
                            {bidder: "richaudience", params: {pid: "7XR5aSL5vQ", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335828, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '125170'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InFeed_4_300x250'}},
                        ]
                    }
                );

            }

            // **************** InFeed_5 GPT and Prebid definition. *******************

            else if (el.id === 'InFeed_5') { // Check if InFeed_5 div is present in body. 

                // InFeed_5 ad unit definition
                InFeed_5 = googletag.defineSlot(adUnitPath + 'InFeed_5', [[300, 250]], 'InFeed_5')
                                        .defineSizeMapping(inFeed_mapping)
                                        .setTargeting('position', 'InFeed_5')
                                        .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                        .addService(googletag.pubads());
                
                // Push the defined InFeed_5 Prebid placement IDs to the ATF array.      
                // Lazy-loaded: below-the-fold array (see InFeed_2 for why).
                adUnitsBTF.push(
                    {   code: 'InFeed_5',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[580, 280], [300, 250], [336, 280]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[300, 250], [336, 280]] }, //tablet
                            { minViewPort: [0, 0], sizes: [] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-2wrqmhjbwreqgk"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890474', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265057', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094778'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706114'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851830'}},
                            {bidder: "richaudience", params: {pid: "1HxXvaMBiA", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335830, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '125171'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InFeed_5_300x250'}},
                        ]
                    }
                );

            }

            // **************** InFeed_6 GPT and Prebid definition. *******************

            else if (el.id === 'InFeed_6') { // Check if InFeed_6 div is present in body. 

                // InFeed_6 ad unit definition
                InFeed_6 = googletag.defineSlot(adUnitPath + 'InFeed_6', [[300, 250]], 'InFeed_6')
                                        .defineSizeMapping(inFeed_mapping)
                                        .setTargeting('position', 'InFeed_6')
                                        .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                        .addService(googletag.pubads());
                
                // Push the defined InFeed_6 Prebid placement IDs to the ATF array.      
                // Lazy-loaded: below-the-fold array (see InFeed_2 for why).
                adUnitsBTF.push(
                    {   code: 'InFeed_6',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[580, 280], [300, 250], [336, 280]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[300, 250], [336, 280]] }, //tablet
                            { minViewPort: [0, 0], sizes: [] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wd-hb-stdb-blic-ringi-2wrqmhjbwreqgk"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1915313', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265058', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1106784'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560837330'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5961731'}},
                            {bidder: "richaudience", params: {pid: "PKZ71mbA3v", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3405530, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '125293'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InFeed_6_300x250'}},
                        ]
                    }
                );

            }

            // **************** InText_1 GPT and Prebid definition. *******************

            else if (el.id === 'InText_1') { // Check if InText_1 div is present in body. 

                // InText_1 ad unit definition
                InText_1 = googletag.defineSlot(adUnitPath + 'InText_1', [[300, 250]], 'InText_1')
                                     .defineSizeMapping(inText_mapping)
                                     .setTargeting('position', 'InText_1')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());
                
                // Push the defined InText_1 Prebid placement IDs to the ATF array.      
                adUnitsATF.push(
                    {   code: 'InText_1',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280], [580, 280], [1, 1]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[468, 60], [200, 200], [250, 250], [300, 250], [336, 280], [300, 50], [320, 50], [1, 1]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100], [200, 200], [250, 250], [300, 250], [336, 280], [1, 1]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-iart-blic-ringi-tcb2y9dhkyehha"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890479', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265059', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984450', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094779'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706115'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851831'}},
                            {bidder: "richaudience", params: {pid: "vJz8xCyi5h", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335832, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '120627'}},
                            {bidder: 'teads', params: {pageId: 182480, placementId: 197485}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InText_1_300x250'}},
                        ]
                    }
                );

            }   

            // **************** InText_2 GPT and Prebid definition. *******************

            else if (el.id === 'InText_2') { // Check if InText_2 div is present in body. 

                // InText_2 ad unit definition
                InText_2 = googletag.defineSlot(adUnitPath + 'InText_2', [[300, 250]], 'InText_2')
                                     .defineSizeMapping(inText_mapping)
                                     .setTargeting('position', 'InText_2')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());
                
                // Push the defined InText_2 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'InText_2',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280], [580, 280], [1, 1]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[468, 60], [200, 200], [250, 250], [300, 250], [336, 280], [300, 50], [320, 50], [1, 1]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100], [200, 200], [250, 250], [300, 250], [336, 280], [1, 1]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-iart-blic-ringi-tcb2y9dhkyehha"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890480', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265060', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984450', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094782'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706116'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851832'}},
                            {bidder: "richaudience", params: {pid: "OJfmAXN4In", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335834, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '120628'}},
                            {bidder: 'teads', params: {pageId: 182480, placementId: 197485}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InText_2_300x250'}},
                        ]
                    }
                );

            }   

            // **************** InText_3 GPT and Prebid definition. *******************

            else if (el.id === 'InText_3') { // Check if InText_3 div is present in body. 

                // InText_3 ad unit definition
                InText_3 = googletag.defineSlot(adUnitPath + 'InText_3', [[300, 250]], 'InText_3')
                                     .defineSizeMapping(inText_mapping)
                                     .setTargeting('position', 'InText_3')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());
                
                // Push the defined InText_3 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'InText_3',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280], [580, 280], [1, 1]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[468, 60], [200, 200], [250, 250], [300, 250], [336, 280], [300, 50], [320, 50], [1, 1]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100], [200, 200], [250, 250], [300, 250], [336, 280], [1, 1]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-iart-blic-ringi-tcb2y9dhkyehha"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890481', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265061', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984450', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094784'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706117'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851833'}},
                            {bidder: "richaudience", params: {pid: "3MPRmqNAAQ", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335836, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '120629'}},
                            {bidder: 'teads', params: {pageId: 182480, placementId: 197485}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InText_3_300x250'}},
                        ]
                    }
                );

            }  

            // **************** InText_4 GPT and Prebid definition. *******************

            else if (el.id === 'InText_4') { // Check if InText_4 div is present in body. 

                // InText_4 ad unit definition
                InText_4 = googletag.defineSlot(adUnitPath + 'InText_4', [[300, 250]], 'InText_4')
                                     .defineSizeMapping(inText_mapping)
                                     .setTargeting('position', 'InText_4')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());
                
                // Push the defined InText_4 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'InText_4',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280], [580, 280], [1, 1]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[468, 60], [200, 200], [250, 250], [300, 250], [336, 280], [300, 50], [320, 50], [1, 1]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100], [200, 200], [250, 250], [300, 250], [336, 280], [1, 1]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-iart-blic-ringi-tcb2y9dhkyehha"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890482', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265062', priceType: 'net'}},
                            {bidder: 'mediapool', params: {mid: '1984421', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094787'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706118'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851834'}},
                            {bidder: "richaudience", params: {pid: "nqI5uTqrmX", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335838, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '124582'}},
                            {bidder: 'teads', params: {pageId: 182480, placementId: 197485}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InText_4_300x250'}},
                        ]
                    }
                );

            }  

            // **************** InText_5 GPT and Prebid definition. *******************

            else if (el.id === 'InText_5') { // Check if InText_5 div is present in body. 

                // InText_5 ad unit definition
                InText_5 = googletag.defineSlot(adUnitPath + 'InText_5', [[300, 250]], 'InText_5')
                                     .defineSizeMapping(inText_mapping)
                                     .setTargeting('position', 'InText_5')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());
                
                // Push the defined InText_5 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'InText_5',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280], [580, 280], [1, 1]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[468, 60], [200, 200], [250, 250], [300, 250], [336, 280], [300, 50], [320, 50], [1, 1]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100], [200, 200], [250, 250], [300, 250], [336, 280], [1, 1]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-iart-blic-ringi-tcb2y9dhkyehha"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890483', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265063', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094788'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706119'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851835'}},
                            {bidder: "richaudience", params: {pid: "EBSl1gnnRv", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335840, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '124583'}},
                            {bidder: 'teads', params: {pageId: 182480, placementId: 197485}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InText_5_300x250'}},
                        ]
                    }
                );

            }  

            // **************** InText_6 GPT and Prebid definition. *******************

            else if (el.id === 'InText_6') { // Check if InText_6 div is present in body. 

                // InText_6 ad unit definition
                InText_6 = googletag.defineSlot(adUnitPath + 'InText_6', [[300, 250]], 'InText_6')
                                     .defineSizeMapping(inText_mapping)
                                     .setTargeting('position', 'InText_6')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());
                
                // Push the defined InText_6 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'InText_6',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280], [580, 280], [1, 1]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[468, 60], [200, 200], [250, 250], [300, 250], [336, 280], [300, 50], [320, 50], [1, 1]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100], [200, 200], [250, 250], [300, 250], [336, 280], [1, 1]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-iart-blic-ringi-tcb2y9dhkyehha"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890485', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265064', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094790'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706120'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851836'}},
                            {bidder: "richaudience", params: {pid: "1YOEI2gbuC", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335842, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '125172'}},
                            {bidder: 'teads', params: {pageId: 182480, placementId: 197485}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InText_6_300x250'}},
                        ]
                    }
                );

            }  

            // **************** InText_7 GPT and Prebid definition. *******************

            else if (el.id === 'InText_7') { // Check if InText_7 div is present in body. 

                // InText_7 ad unit definition
                InText_7 = googletag.defineSlot(adUnitPath + 'InText_7', [[300, 250]], 'InText_7')
                                     .defineSizeMapping(inText_mapping)
                                     .setTargeting('position', 'InText_7')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());
                
                // Push the defined InText_7 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'InText_7',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280], [580, 280], [1, 1]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[468, 60], [200, 200], [250, 250], [300, 250], [336, 280], [300, 50], [320, 50], [1, 1]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100], [200, 200], [250, 250], [300, 250], [336, 280], [1, 1]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-iart-blic-ringi-tcb2y9dhkyehha"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890486', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265065', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094791'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706121'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851837'}},
                            {bidder: "richaudience", params: {pid: "S5JVKrDXNr", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335844, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '125173'}},
                            {bidder: 'teads', params: {pageId: 182480, placementId: 197485}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InText_7_300x250'}},
                        ]
                    }
                );

            }  

            // **************** InText_8 GPT and Prebid definition. *******************

            else if (el.id === 'InText_8') { // Check if InText_8 div is present in body. 

                // InText_8 ad unit definition
                InText_8 = googletag.defineSlot(adUnitPath + 'InText_8', [[300, 250]], 'InText_8')
                                     .defineSizeMapping(inText_mapping)
                                     .setTargeting('position', 'InText_8')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());
                
                // Push the defined InText_8 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'InText_8',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280], [580, 280], [1, 1]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[468, 60], [200, 200], [250, 250], [300, 250], [336, 280], [300, 50], [320, 50], [1, 1]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100], [200, 200], [250, 250], [300, 250], [336, 280], [1, 1]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-iart-blic-ringi-tcb2y9dhkyehha"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890487', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265066', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094793'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706122'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851838'}},
                            {bidder: "richaudience", params: {pid: "ZHTLXsLSW3", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335846, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '125174'}},
                            {bidder: 'teads', params: {pageId: 182480, placementId: 197485}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InText_8_300x250'}},
                        ]
                    }
                );

            }  

            // **************** InText_9 GPT and Prebid definition. *******************

            else if (el.id === 'InText_9') { // Check if InText_9 div is present in body. 

                // InText_9 ad unit definition
                InText_9 = googletag.defineSlot(adUnitPath + 'InText_9', [[300, 250]], 'InText_9')
                                     .defineSizeMapping(inText_mapping)
                                     .setTargeting('position', 'InText_9')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());
                
                // Push the defined InText_9 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'InText_9',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280], [580, 280], [1, 1]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[468, 60], [200, 200], [250, 250], [300, 250], [336, 280], [300, 50], [320, 50], [1, 1]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100], [200, 200], [250, 250], [300, 250], [336, 280], [1, 1]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-iart-blic-ringi-tcb2y9dhkyehha"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890490', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265067', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094794'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706123'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851839'}},
                            {bidder: "richaudience", params: {pid: "dE5jUeyEXX", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335848, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '125175'}},
                            {bidder: 'teads', params: {pageId: 182480, placementId: 197485}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InText_9_300x250'}},
                        ]
                    }
                );

            }  

            // **************** InText_10 GPT and Prebid definition. *******************

            else if (el.id === 'InText_10') { // Check if InText_10 div is present in body. 

                // InText_10 ad unit definition
                InText_10 = googletag.defineSlot(adUnitPath + 'InText_10', [[300, 250]], 'InText_10')
                                     .defineSizeMapping(inText_mapping)
                                     .setTargeting('position', 'InText_10')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());
                
                // Push the defined InText_10 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'InText_10',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280], [580, 280], [1, 1]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[468, 60], [200, 200], [250, 250], [300, 250], [336, 280], [300, 50], [320, 50], [1, 1]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100], [200, 200], [250, 250], [300, 250], [336, 280], [1, 1]] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-iart-blic-ringi-tcb2y9dhkyehha"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1890491', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265068', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1094795'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560706124'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '5851840'}},
                            {bidder: "richaudience", params: {pid: "HTPo4cEP5f", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3335850, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '125176'}},
                            {bidder: 'teads', params: {pageId: 182480, placementId: 197485}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_InText_10_300x250'}},
                        ]
                    }
                );

            }  

            // **************** Under_Article_1 GPT and Prebid definition. *******************

            else if (el.id === 'Under_Article_1') { // Check if Under_Article_1 div is present in body. 

                // Under_Article_1 ad unit definition
                Under_Article_1 = googletag.defineSlot(adUnitPath + 'Under_Article_1', [[300, 250]], 'Under_Article_1')
                                     .defineSizeMapping(underArticle_mapping)
                                     .setTargeting('position', 'Under_Article_1')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .setTargeting('refresh_interval', INITIAL_REFRESH_INTERVAL_TARGETING_VALUE)
                                    .setTargeting('refresh_policy', INITIAL_REFRESH_POLICY_TARGETING_VALUE)
                                     .addService(googletag.pubads());
                
                // Push the defined Under_Article_1 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'Under_Article_1',
                        mediaTypes: {banner: {
                        // Sizes aligned to underArticle_mapping on 05.09.2026: the GPT
                        // slot accepts no 580x280 on desktop and no 320x50 on tablet,
                        // so a Prebid win at either size could never render (same
                        // defect class as the Billboard tablet band fixed on 02.09).
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[200, 200], [250, 250], [300, 250], [336, 280], [300, 50]] }, //tablet
                            { minViewPort: [0, 0], sizes: [] }, //mobile
                        ]
                        }},
                        bids: [
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-iart-blic-ringi-tcb2y9dhkyehha"}, labelAny: ["desktop"]},
                            {bidder: "ogury", params: {assetKey: "OGY-0F3B193368B3", adUnitId: "wm-hb-stdb-blic-ringi-dxxtximjqge0gr"}, labelAny: ["mobile"]},
                            {bidder: 'adf', params: {mid: '1940757', priceType: 'net'}},
                            {bidder: 'adf', params: {mid: '2265069', priceType: 'net'}},
                            {bidder: 'connectad', params: {networkId: 44, siteId: 3213233}},
                            {bidder: "eskimi", params: {placementId: 1932}},
                            {bidder: 'ix', params: {siteId: '1121027'}},
                            {bidder: 'openx', params: {delDomain: 'ringieraxelspr-d.openx.net', unit: '560937979'}},
                            {bidder: 'pubmatic', params: {publisherId: '158892', adSlot: '6048963'}},
                            {bidder: "richaudience", params: {pid: "M9mfVnQ684", supplyType: "site"}},
                            {bidder: "rtbhouse", params: {region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10}},
                            {bidder: 'magnite', params: {accountId: 19712, siteId: 540110, zoneId: 3467436, floor: 0.1 } },
                            {bidder: 'equativ', params: {networkId: '4527', siteId: '651320', pageId: '1967211', formatId: '133077'}},
                            {bidder: 'teads', params: {pageId: 182479, placementId: 197484}},
                            {bidder: 'taboola', params: {publisherId: '2039401', tagId: '100008128_Under_Article_1_300x250'}},
                        ]
                    }
                );

            }  

        }); // End of wrapperAd divs check. 

        //Create sticky ad once the ad is ready to render
        googletag.pubads().addEventListener('slotRenderEnded', function(event) {

            let unitPath = event.slot.getAdUnitPath(); //get the ad unith path for the slot which will render the ad
            let stickyBackground = document.getElementById('adhesion_background'); //get the adhesion background id
            //let windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth; //check user's screen width

            if (unitPath === adUnitPath + "Sticky" && event.isEmpty === true) {
                let sticky = document.getElementById('Adhesion');
                sticky.style.display = 'none';
            }

            // if ad unit is sticky and the background is not set, set sticky CSS for sticky ad unit
            else if (unitPath === adUnitPath + "Sticky" && stickyBackground === null){
            let sticky = document.getElementById('Adhesion');
            sticky.style.position = 'fixed';
            sticky.style.zIndex = '999';
            sticky.style.width = '100%';
            sticky.style.height = '';
            sticky.style.bottom = '0px';
            sticky.style.textAlign = 'center';	
            sticky.style.borderTop = '1px #CCC solid';
            
            // create adhesion background
            let background = document.createElement("div");
            background.setAttribute("ID", "adhesion_background");
            background.style.backgroundColor = '#f7f7f7';
            
            if (adWindowWidth > 768) {
                background.style.opacity = '70%';
                sticky.style.padding = '10px 0px 0px';
            }

            else {
                background.style.opacity = '100%';
                sticky.style.padding = '10px 0px 20px';
                sticky.style.bottom = '50px'; //adjust the position above the navigation mobile menu
            }

            background.style.position = 'absolute';
            background.style.height = '100%';
            background.style.width = '100%';
            background.style.top = '0';
            background.style.left = '0';
            background.style.zIndex = '-1';
            sticky.appendChild(background);

            // create placeholder text
            /*let placeholder = document.createElement("div");
            placeholder.setAttribute("ID", "adText");
            placeholder.style.textAlign = 'center';
            placeholder.style.fontSize = '10px';
            placeholder.style.color = '#CCC';
            placeholder.style.height = '20px';
            placeholder.style.borderTop = '1px #CCC solid';
            placeholder.style.paddingTop = '2px';
            placeholder.textContent = 'Oglas'
            background.appendChild(placeholder);*/
        
            // create close button with svg elements
            let closeButton = document.createElement("div");
            closeButton.setAttribute("ID", "close_adhesion")
            closeButton.style.position = 'absolute';
            closeButton.style.top = '-29px';
            closeButton.style.right = '0px';
            closeButton.style.cursor = 'pointer';
            closeButton.style.padding = '8px';
            //closeButton.style.borderRadius = '50%';
            closeButton.style.border = '1px solid #CCC';
            //closeButton.style.boxShadow = '0 0 3px #b8b9be';
            closeButton.style.background = '#f7f7f7';
            closeButton.style.borderBottom = '1px solid #f7f7f7';
            closeButton.style.borderTopLeftRadius = '10px';
            
            let iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            let iconPath = document.createElementNS(
                'http://www.w3.org/2000/svg',
                'path'
            );

            iconSvg.setAttribute('display', 'block');
            iconSvg.setAttribute('fill', '#000');
            iconSvg.setAttribute('width', '12px');
            iconSvg.setAttribute('height', '12px');
            iconSvg.setAttribute('viewBox', '0 0 8 8');

            iconPath.setAttribute(
                'd',
                'M1.41 0l-1.41 1.41.72.72 1.78 1.81-1.78 1.78-.72.69 1.41 1.44.72-.72 1.81-1.81 1.78 1.81.69.72 1.44-1.44-.72-.69-1.81-1.78 1.81-1.81.72-.72-1.44-1.41-.69.72-1.78 1.78-1.81-1.78-.72-.72z'
            );
            
            iconSvg.appendChild(iconPath);
            closeButton.appendChild(iconSvg); 
            sticky.appendChild(closeButton);
            
            // when user clicks the close button, remove the sticky div
            document.getElementById("close_adhesion").addEventListener('click', function(e) {
        e.preventDefault();

        if (sticky && sticky.parentNode) {
            sticky.parentNode.removeChild(sticky);
        }
    }, false); 


            } 
        });

        // Check if adsKeywords object is present
        if (typeof adsKeywords != 'undefined') {

            // CX targeting
            if(typeof window.localStorage === 'object' && typeof window.localStorage.getItem === 'function' && localStorage.getItem("CxSegments") !== null && localStorage.getItem("CxSegments").length>0) {
                googletag.pubads().setTargeting('CxSegments', localStorage.getItem('CxSegments').split(','));
            }

            if(typeof window.localStorage === 'object' && typeof window.localStorage.getItem === 'function' && localStorage.getItem("CxCmbnd") !== null && localStorage.getItem("CxCmbnd").length>0) {
                googletag.pubads().setTargeting('CxCmbnd', localStorage.getItem('CxCmbnd').split(','))
            }

            // Check if adsKeywords attributes are present, if so, pass them as key values to GAM
            if (typeof adsKeywords.category != 'undefined') {
                googletag.pubads().setTargeting('category', adsKeywords.category);
            } 

            if (typeof adsKeywords.subcategory != 'undefined') {
                googletag.pubads().setTargeting('subcategory', adsKeywords.subcategory);
            } 

            if (typeof adsKeywords.topic != 'undefined') {
                googletag.pubads().setTargeting('topic', adsKeywords.topic);
            } 

            if (typeof adsKeywords.pageType != 'undefined') {
                googletag.pubads().setTargeting('pageType', adsKeywords.pageType);
            } 

            if (typeof adsKeywords.articleUuid != 'undefined') {
                googletag.pubads().setTargeting('articleId', [adsKeywords.articleUuid].join(','));
            } 
            
            if (typeof adsKeywords.articleTags != 'undefined') {
                googletag.pubads().setTargeting('articleTags', [adsKeywords.articleTags].join(','));
            }
        }

        let adQueryString = window.location.search; // Get the whole string in the URL. 
        //console.log("Query string is " + adQueryString);

        let adUrlParams = new URLSearchParams(adQueryString); // Get the information after ? in the URL. 
        //console.log("URL parameter is " +  adUrlParams);

        if (adUrlParams.has('adTest')) { 
            let adValue = adUrlParams.get('adTest'); // If there is an adTest parameter, get the value after =.
            //console.log("AdTest value is " + adValue);
            googletag.pubads().setTargeting('adTest', adValue); // Send the adTest value to GAM.
        } 

        if (adUrlParams.has('utm_source')) {
            let utmSource = adUrlParams.get('utm_source'); // If there is a utm_source parameter, get the value after =.
            //console.log("UTM source value is " + utmSource);
            googletag.pubads().setTargeting('utmSource', utmSource); // Send the utm_source value to GAM.
        } 

        if (adUrlParams.has('utm_medium')) {
            let utmMedium = adUrlParams.get('utm_medium'); // If there is a utm_medium parameter, get the value after =.
            //console.log("UTM medium value is " + utmMedium);
            googletag.pubads().setTargeting('utmMedium', utmMedium); // Send the utm_medium value to GAM.
        } 

        let refURL = document.referrer; // Get the URL from which the user came. 

        if (refURL.length === 0) {
            googletag.pubads().setTargeting('referrer', 'direct');
        } 

        else if (refURL.includes("google")) {
            googletag.pubads().setTargeting('referrer', 'google');
        } 
          
        else if (refURL.includes("linkedin") || refURL.includes("t.com") || refURL.includes("facebook") || refURL.includes("instagram")) {
            googletag.pubads().setTargeting('referrer', 'social');
        } 
          
        else if (refURL.includes("blic.rs") || refURL.includes("pulsonline.rs") || refURL.includes("ana.rs") || refURL.includes("nekretnine.rs") || refURL.includes("mojauto.rs")) {
            googletag.pubads().setTargeting('referrer', 'ringier');
        } 
          
        else {
            googletag.pubads().setTargeting('referrer', 'other');
        }

        installBlicRefreshDebugListener();

        // Registered before enableServices() so no render event is missed.
        // This has to run whether or not debug logging is enabled, because the
        // aq_prev_fill / aq_prev_source values depend on it.
        installBlicAuctionQualityRenderListener();

        googletag.pubads().disableInitialLoad(); // Disables requests for ads on page load.
        googletag.pubads().enableSingleRequest(); // Enables single request mode for fetching multiple ads at the same time.
        googletag.pubads().setCentering(true);
        googletag.enableServices(); // Enables all GPT services that have been defined for ad slots on the page.

        // Get the list of slots associated with this service.
        googletag.pubads().getSlots().forEach(function(adSlots) {
            googletag.display(adSlots.getSlotElementId()); // Instructs slot services to render all of the defined ad slots. 
        });

    });

    // =========================================================================
    // DEVICE LABELS FOR BIDDER-LEVEL DESKTOP / MOBILE TARGETING (fixed 05.09.2026)
    // =========================================================================
    //
    // Every Ogury entry below carries labelAny: ["desktop"] or ["mobile"] so that
    // only the placement id for the visitor's device is requested. Until this
    // release the labels were expected to come from the sizeConfig block in
    // pbjs.setConfig (mediaQuery -> labels). That mechanism belongs to Prebid's
    // legacy size mapping. This build compiles the sizeMappingV2 module, and
    // because every ad unit here uses mediaTypes.banner.sizeConfig, V2 handles
    // the ad units -- and V2 evaluates labelAny / labelAll ONLY against the
    // labels array passed to pbjs.requestBids(). The setConfig labels were never
    // consulted, so both Ogury placements fired on every device (verified live
    // on 05.09.2026: a desktop page requested the wd-... and the wm-... id on
    // every slot). Half of Ogury's requests were for the wrong device.
    //
    // The fix is to hand the active device label to every requestBids call.
    // The breakpoint mirrors the labels block kept in setConfig below (1024 px)
    // and the wrapper's own size mappings (desktop starts at 1024 px).
    function getPrebidDeviceLabels() {
        try {
            if (window.matchMedia && window.matchMedia('(min-width: 1024px)').matches) {
                return ['desktop'];
            }
        } catch (matchMediaError) {
            // Fall through to the mobile label; a failed media query must never
            // block an auction.
        }
        return ['mobile'];
    }

    pbjs.que.push(function() {

        pbjs.aliasBidder('adf', 'mediapool', {gvlid:50});

        pbjs.setConfig({

            priceGranularity: 'high', //$0.01 increments, up to $20

            // Device labels for the LEGACY size mapping only. Kept so the
            // intent stays visible in the config; the sizeMappingV2 module used
            // by this build ignores this block and reads the labels passed to
            // pbjs.requestBids() instead -- see getPrebidDeviceLabels() above.
            sizeConfig: [
                {
                    mediaQuery: '(min-width: 1024px)',
                    labels: ['desktop']
                },
                {
                    mediaQuery: '(max-width: 1023px)',
                    labels: ['mobile']
                }
            ],

            enableSendAllBids: false, //send only top bids
            targetingControls: {
              alwaysIncludeDeals: true //send all deal bids
            },
    
            //Currency module
            currency: {
                "adServerCurrency": "EUR",
                "conversionRateFile": "https://cdn.jsdelivr.net/gh/prebid/currency-file@1/latest.json",
             },
    
            //Coockie match and user syncs
            userSync: {
                syncEnabled: true,
                syncsPerBidder: 5,
                syncDelay: 3000,
                auctionDelay: 50,
                userIds: [
    
                    { name: "sharedId", 
                      storage: { type: "cookie", name: "_sharedid", expires: 365 } 
                    },
    
                    { name: "id5Id",
                      params: { partner: 1355, externalModuleUrl: 'https://cdn.id5-sync.com/api/1.0/id5PrebidModule.js' },
                      storage: { type: "html5", name: "id5id", expires: 90, refreshInSeconds: 8*3600 }
                    },
    
                    { name: 'teadsId',
                      params: { pubId: 26849 }
                    },
        
                    { name: 'lotamePanoramaId',
                    },
    
                ],
                aliasSyncEnabled: true,
                filterSettings: { all: { bidders: "*", filter: "include" } },
            },

            enableTIDs: true,

            // defaultGdprScope is deliberately false: if the CMP never answers
            // (and even the TCF watchdog at the top of this file somehow fails
            // to take over), Prebid proceeds after the timeout treating GDPR as
            // not applying, which is the correct resolution for this site's
            // non-EEA (Serbian) audience. With defaultGdprScope true, a stuck
            // CMP means "GDPR applies with zero consent" and every auction
            // dies — that is exactly the failure mode that took the site's ads
            // down on 03.09.2026.
            consentManagement: {
                gdpr: {
                  cmpApi: 'iab',
                  timeout: 8000,
                  defaultGdprScope: false
                }
            },

        });

        pbjs.bidderSettings = {
            standard: {
                storageAllowed: true,
            },

            // Step 4 of Taboola's Blic integration guidelines: the Taboola bidder
            // must be granted localStorage access. The standard block above already
            // grants it to every bidder, so this is technically redundant today, but
            // Taboola asks for it per-bidder and declaring it explicitly keeps the
            // requirement visible if the standard default is ever tightened.
            //
            // Note the key is lowercase 'taboola' so that it matches the bidder code
            // used in the ad units. Taboola's own documentation writes it as
            // bidderSettings["Taboola"], which would silently never apply, since
            // Prebid matches these keys against the bidder code exactly.
            taboola: {
                storageAllowed: true,

                // Step 5 of Taboola's guidelines: multiply Taboola's bids by 0.75 to
                // convert them from gross to net. Taboola states this is required
                // ONLY when every other SSP in the setup already bids net, and that
                // is the case on Blic -- confirmed for the whole header bidding stack
                // on 24.08.2026. So it applies here and is enabled.
                //
                // Without it, Taboola's gross bids would sit roughly 33% high against
                // every other bidder, and Taboola would win auctions it should lose,
                // displacing demand that is genuinely worth more. A bidder winning on
                // an inflated bid looks like strong performance in reporting while
                // actually costing money, which makes this easy to miss.
                //
                // If the stack ever moves to gross bidding, this multiplier has to be
                // removed at the same time, or Taboola will be penalised by 25%.
                bidCpmAdjustment: function(bidCpm) {
                    return bidCpm * 0.75;
                },
            },
        };
        
        //Add only ATF ad units.
        pbjs.addAdUnits(adUnitsATF);

        // Split initial ATF auction so Billboard can use a shorter timeout without changing other ATF slots.
        var initialBillboardAdUnitCodes = adUnitsATF
            .filter(function(adUnit) { return adUnit.code === 'Billboard'; })
            .map(function(adUnit) { return adUnit.code; });

        var initialOtherATFAdUnitCodes = adUnitsATF
            .filter(function(adUnit) { return adUnit.code !== 'Billboard'; })
            .map(function(adUnit) { return adUnit.code; });

        // Set PPID on a best-effort basis and immediately refresh the initial GPT slots.
        // Initial ad delivery must never wait for async user-ID resolution.
        //
        // The wrapper runs with disableInitialLoad(), so this is the slots' very
        // first ad request and it goes through refresh() like any later one. The
        // aq_* values of the auction that has just finished are therefore
        // attached here too, not only on refreshes. Since nothing has rendered
        // in these slots yet, aq_prev_fill and aq_prev_source resolve to
        // 'unknown' on their own.
        function refreshInitialAdSlots(
            adUnitCodes,
            gptSlots,
            auctionBidResponses,
            auctionTimedOut,
            auctionIdentifier,
            auctionRequestKind
        ) {
            googletag.cmd.push(function() {

                try {
                    var storedId5 = window.localStorage.getItem('id5id');

                    if (storedId5) {
                        var parsedId5 = JSON.parse(decodeURIComponent(storedId5));
                        var ppid = parsedId5 && parsedId5.gp;

                        if (ppid) {
                            googletag.pubads().setPublisherProvidedId(ppid);
                        }
                    }
                }

                catch (err) {
                    // PPID is optional; continue with the initial ad request.
                }

                pbjs.setTargetingForGPTAsync(adUnitCodes);

                var definedSlots = gptSlots.filter(function(slot) { return !!slot; });

                // Must stay directly before refresh(): GPT reads slot targeting
                // when the request is built.
                applyAuctionQualityToBatchedSlots(
                    definedSlots,
                    adUnitCodes,
                    auctionBidResponses,
                    auctionTimedOut,
                    auctionIdentifier,
                    auctionRequestKind
                );

                if (definedSlots.length) {
                    googletag.pubads().refresh(definedSlots);
                }
            });
        }

        // Start the rest of ATF immediately with the standard timeout.
        // This protects Sticky/P1/InText_1 from being deprioritized by the Billboard timeout test.
        if (initialOtherATFAdUnitCodes.length) {
            pbjs.requestBids({
                adUnitCodes: initialOtherATFAdUnitCodes,
                // Device label so that labelAny on the bids is honoured (see
                // getPrebidDeviceLabels above).
                labels: getPrebidDeviceLabels(),
                bidsBackHandler: function(bidResponses, timedOut, auctionId) {
                    refreshInitialAdSlots(
                        initialOtherATFAdUnitCodes,
                        [Adhesion, InFeed_1, P1, InText_1],
                        bidResponses,
                        timedOut,
                        auctionId,
                        'initial_atf'
                    );
                },
                timeout: PREBID_TIMEOUT
            });
        }

        // Start Billboard as a separate initial auction with a softer timeout than the previous 1000ms test.
        if (initialBillboardAdUnitCodes.length) {
            pbjs.requestBids({
                adUnitCodes: initialBillboardAdUnitCodes,
                labels: getPrebidDeviceLabels(),
                // Billboard keeps its own parallel auction, so its aq_* values are
                // built from this auction's responses only and can never be mixed
                // with the rest-of-ATF auction above.
                bidsBackHandler: function(bidResponses, timedOut, auctionId) {
                    refreshInitialAdSlots(
                        initialBillboardAdUnitCodes,
                        [Billboard, Branding_Left, Branding_Right],
                        bidResponses,
                        timedOut,
                        auctionId,
                        'initial_billboard'
                    );
                },
                timeout: BILLBOARD_PREBID_TIMEOUT
            });
        }
    });

    //Add BTF ad units.
    pbjs.que.push(function() {
        pbjs.addAdUnits(adUnitsBTF);
    });

    // Use IntersectionObserver API to check whether an ad slot is at the certain distance. 
    var observer = new IntersectionObserver (function(entries) {

        entries.forEach(entry => {

            if (entry.isIntersecting) {

                let adSlot = entry.target.id; // Get the div id of the slot being observed.

                // A slot that belongs to the initial above-the-fold auction has
                // already been auctioned and requested when this observer fires
                // (the page template gives InFeed_1 the lazyAd class on section
                // pages although the wrapper treats it as above the fold). Until
                // 05.09.2026 such a slot got a second, useless 14-bidder auction
                // here and, for InFeed_1, no GPT refresh to go with it. Skip it;
                // the viewability refresh logic takes it from here.
                var alreadyAuctionedAboveTheFold = adUnitsATF.some(function(adUnit) {
                    return adUnit.code === adSlot;
                });

                if (alreadyAuctionedAboveTheFold) {
                    blicRefreshDebug('LAZY LOAD SKIPPED', {
                        slot: adSlot,
                        reason: 'slot is part of the initial above-the-fold auction'
                    });
                    observer.unobserve(entry.target);
                    return;
                }

                // Request bids and push the ads to GAM.
                pbjs.que.push(function() {
                    pbjs.requestBids({
                        timeout: PREBID_TIMEOUT,
                        adUnitCodes: [adSlot],
                        labels: getPrebidDeviceLabels(),
                        bidsBackHandler: function(bidResponses, timedOut, auctionId) {
                            pbjs.setTargetingForGPTAsync([adSlot]);

                            // This is the first ad request for a lazy-loaded slot,
                            // so it needs the aq_* values of the auction that has
                            // just finished, exactly like a refresh does. Applied
                            // here, immediately before the refresh() calls below.
                            collectAndApplyAuctionQuality(
                                adSlot,
                                findGptSlotBySlotElementId(adSlot),
                                bidResponses,
                                timedOut,
                                auctionId,
                                'initial_lazy'
                            );

                            // Request the GPT slot that belongs to this div. The slot
                            // is looked up by its element id instead of the former
                            // hand-maintained if/else ladder of every slot name: the
                            // ladder silently gave any slot missing from it (as
                            // InFeed_1 was) a Prebid auction with no ad request.
                            var lazyLoadedGptSlot = findGptSlotBySlotElementId(adSlot);

                            if (lazyLoadedGptSlot) {
                                googletag.pubads().refresh([lazyLoadedGptSlot]);
                            }

                            else {
                                blicRefreshDebug('LAZY LOAD WITHOUT GPT SLOT', {
                                    slot: adSlot,
                                    message: 'No GPT slot is defined for this div id; nothing to request.'
                                });
                            }

                        }
                    });
                });
                observer.unobserve(entry.target); // Stop observing the ad slot which has alredy been seen. 
            }
        })
    }, {rootMargin: DISTANCE_IN_PIXELS_FOR_LAZY_LOADING}); // Distance to the ad slot.

    document.querySelectorAll('.lazyAd').forEach(slots => { observer.observe(slots) }); // Check all divs with the class lazyAd and observe them all. 

    
    // Get the list of slots associated with this service for the refresh logic.
    googletag.pubads().getSlots().forEach(function(managedSlot) {

        var managedSlotElementId = managedSlot.getSlotElementId(); // Get the ID of the ad slot in question.
        var completedRefreshCount = parseInt(managedSlot.getTargeting('refresh_count')[0], 10);
        var refreshTimeout = null;
        var isSlotInView = false;
        var isAuctionRunning = false;
        var currentSlotAdvertiserId = null;
        var billboardRefreshPolicyIsReady = managedSlotElementId !== TOP_BILLBOARD_SLOT_ELEMENT_ID;
        var isWaitingForBillboardRenderEvent = false;
        var billboardRenderWatchdogTimeout = null;

        if (isNaN(completedRefreshCount)) {
            completedRefreshCount = 0;
        }

        function clearRefreshTimeout() {
            if (refreshTimeout !== null) {
                clearTimeout(refreshTimeout);
                refreshTimeout = null;
            }
        }

        function clearBillboardRenderWatchdog() {
            if (billboardRenderWatchdogTimeout !== null) {
                clearTimeout(billboardRenderWatchdogTimeout);
                billboardRenderWatchdogTimeout = null;
            }
        }

        function topBillboardSlotIsAvailable() {
            if (managedSlotElementId !== TOP_BILLBOARD_SLOT_ELEMENT_ID) {
                return true;
            }

            if (document.getElementById(managedSlotElementId) === null) {
                return false;
            }

            var activeSlots = googletag.pubads().getSlots();

            return activeSlots.indexOf(managedSlot) !== -1;
        }

        function initializeBillboardRefreshPolicyFromCurrentResponse() {
            if (managedSlotElementId !== TOP_BILLBOARD_SLOT_ELEMENT_ID) {
                return true;
            }

            if (billboardRefreshPolicyIsReady) {
                return true;
            }

            try {
                var currentResponseInformation = managedSlot.getResponseInformation();

                if (currentResponseInformation === null) {
                    return false;
                }

                currentSlotAdvertiserId = getNormalizedGAMIdentifier(
                    currentResponseInformation.advertiserId
                );
                billboardRefreshPolicyIsReady = true;
                return true;
            }

            catch (error) {
                blicRefreshDebug('ERROR', {
                    slot: managedSlotElementId,
                    message: 'Could not initialize the Billboard policy from GPT response information.',
                    error: error
                });
                return false;
            }
        }

        function startBillboardRenderWatchdog(slotElementId) {
            if (managedSlotElementId !== TOP_BILLBOARD_SLOT_ELEMENT_ID) {
                return;
            }

            if (!isWaitingForBillboardRenderEvent) {
                return;
            }

            clearBillboardRenderWatchdog();

            billboardRenderWatchdogTimeout = setTimeout(function() {
                billboardRenderWatchdogTimeout = null;

                if (!isWaitingForBillboardRenderEvent) {
                    return;
                }

                isWaitingForBillboardRenderEvent = false;
                isAuctionRunning = false;
                billboardRefreshPolicyIsReady = false;

                if (!initializeBillboardRefreshPolicyFromCurrentResponse()) {
                    currentSlotAdvertiserId = null;
                    billboardRefreshPolicyIsReady = true;
                }

                blicRefreshDebug('ERROR', {
                    slot: managedSlotElementId,
                    message: 'GPT did not emit the expected Billboard render event before the watchdog expired. The latest available response will be used.',
                    refreshCount: completedRefreshCount
                });

                scheduleNextRefresh(slotElementId);
            }, BILLBOARD_RENDER_EVENT_WATCHDOG_MILLISECONDS);
        }

        function scheduleNextRefresh(slotElementId) {
            clearRefreshTimeout();

            if (!isSlotInView || isAuctionRunning) {
                blicRefreshDebug('NOT SCHEDULED', {
                    slot: managedSlotElementId,
                    isSlotInView: isSlotInView,
                    isAuctionRunning: isAuctionRunning,
                    refreshCount: completedRefreshCount
                });
                return;
            }

            if (!topBillboardSlotIsAvailable()) {
                blicRefreshDebug('NOT SCHEDULED', {
                    slot: managedSlotElementId,
                    reason: 'The Billboard slot is no longer available.',
                    refreshCount: completedRefreshCount
                });
                return;
            }

            if (
                managedSlotElementId === TOP_BILLBOARD_SLOT_ELEMENT_ID &&
                document.visibilityState !== 'visible'
            ) {
                blicRefreshDebug('NOT SCHEDULED', {
                    slot: managedSlotElementId,
                    reason: 'The browser tab is hidden.',
                    refreshCount: completedRefreshCount
                });
                return;
            }

            if (!initializeBillboardRefreshPolicyFromCurrentResponse()) {
                blicRefreshDebug('WAITING FOR RENDER', {
                    slot: managedSlotElementId,
                    refreshCount: completedRefreshCount
                });
                return;
            }

            // Re-evaluated on every call using whichever advertiser last rendered here,
            // so the 30 second override (or a return to the slot's own default) applies
            // to the upcoming wait regardless of position.
            var refreshPolicyForTimer = getRefreshPolicy(
                managedSlotElementId,
                completedRefreshCount,
                currentSlotAdvertiserId
            );
            var refreshDelaySeconds = refreshPolicyForTimer.refreshDelaySeconds;
            var refreshIntervalTargetingValueForTimer = refreshPolicyForTimer.refreshIntervalTargetingValue;
            var refreshPolicyTargetingValueForTimer = refreshPolicyForTimer.refreshPolicyTargetingValue;

            blicRefreshDebug('SCHEDULED', {
                slot: managedSlotElementId,
                delaySeconds: refreshDelaySeconds,
                advertiserId: currentSlotAdvertiserId,
                refreshPolicy: refreshPolicyTargetingValueForTimer,
                refreshCount: completedRefreshCount,
                refreshBucket: getRefreshBucket(completedRefreshCount)
            });

            refreshTimeout = setTimeout(function () {

                refreshTimeout = null;

                if (!isSlotInView || isAuctionRunning) {
                    blicRefreshDebug('TIMEOUT SKIPPED', {
                        slot: managedSlotElementId,
                        isSlotInView: isSlotInView,
                        isAuctionRunning: isAuctionRunning,
                        refreshCount: completedRefreshCount
                    });
                    return;
                }

                if (!topBillboardSlotIsAvailable()) {
                    blicRefreshDebug('TIMEOUT SKIPPED', {
                        slot: managedSlotElementId,
                        reason: 'The Billboard slot is no longer available.',
                        refreshCount: completedRefreshCount
                    });
                    return;
                }

                // Get response info of the in-view creative.
                // advertiserId is checked against GAM_NOREFRESH_ADVERTISER_IDS.
                // campaignId is the GAM order ID and is checked against GAM_NOREFRESH_ORDER_IDS.
                var currentAdvertiserId = null;
                var currentOrderId = null;

                try {
                    var responseInformation = managedSlot.getResponseInformation();

                    if (responseInformation !== null) {
                        currentAdvertiserId = getNormalizedGAMIdentifier(
                            responseInformation.advertiserId
                        );
                        currentOrderId = getNormalizedGAMIdentifier(
                            responseInformation.campaignId
                        );
                    }
                }

                catch (error) {
                    blicRefreshDebug('ERROR', {
                        slot: managedSlotElementId,
                        message: 'Could not read GPT response information before refresh.',
                        error: error
                    });
                }

                var blockedByAdvertiser = GAM_NOREFRESH_ADVERTISER_IDS.includes(
                    currentAdvertiserId
                );
                var blockedByOrder = GAM_NOREFRESH_ORDER_IDS.includes(currentOrderId);
                var blockedByAdUnit = GAM_NOREFRESH_ADUNITS.includes(managedSlotElementId);
                var browserTabIsVisible = document.visibilityState === 'visible';
                var refreshIsAllowed =
                    !blockedByAdvertiser &&
                    !blockedByOrder &&
                    !blockedByAdUnit &&
                    browserTabIsVisible;

                if (refreshIsAllowed) {

                    isAuctionRunning = true;

                    blicRefreshDebug('AUCTION STARTED', {
                        slot: managedSlotElementId,
                        orderId: currentOrderId,
                        advertiserId: currentAdvertiserId,
                        refreshCount: completedRefreshCount,
                        refreshBucket: getRefreshBucket(completedRefreshCount)
                    });

                    try {
                        pbjs.requestBids({ // Request bids from Prebid partners.
                            timeout: PREBID_TIMEOUT,
                            adUnitCodes: [slotElementId],
                            labels: getPrebidDeviceLabels(),
                            // The Billboard refresh auction requests only the
                            // Billboard ad unit, so its aq_* values are built
                            // from the Billboard's own auction responses.
                            bidsBackHandler: function(bidResponses, timedOut, auctionId) {
                                if (
                                    managedSlotElementId === TOP_BILLBOARD_SLOT_ELEMENT_ID &&
                                    !isAuctionRunning
                                ) {
                                    blicRefreshDebug('STALE AUCTION IGNORED', {
                                        slot: managedSlotElementId,
                                        refreshCount: completedRefreshCount
                                    });
                                    return;
                                }

                                if (
                                    managedSlotElementId === TOP_BILLBOARD_SLOT_ELEMENT_ID &&
                                    (
                                        !isSlotInView ||
                                        document.visibilityState !== 'visible' ||
                                        !topBillboardSlotIsAvailable()
                                    )
                                ) {
                                    isAuctionRunning = false;

                                    blicRefreshDebug('AUCTION RESULT SKIPPED', {
                                        slot: managedSlotElementId,
                                        isSlotInView: isSlotInView,
                                        tabVisible: document.visibilityState === 'visible',
                                        slotAvailable: topBillboardSlotIsAvailable(),
                                        refreshCount: completedRefreshCount
                                    });

                                    scheduleNextRefresh(slotElementId);
                                    return;
                                }

                                // Set refresh targeting for the request that is about to be sent.
                                // GPT reads slot targeting when refresh() is called, so these values must be updated first.
                                var nextRefreshCount = completedRefreshCount + 1;
                                var nextRefreshBucket = getRefreshBucket(nextRefreshCount);
                                var previousRefreshIntervalTargetingValue = managedSlot.getTargeting(
                                    'refresh_interval'
                                )[0];
                                var previousRefreshPolicyTargetingValue = managedSlot.getTargeting(
                                    'refresh_policy'
                                )[0];
                                var previousAuctionQualityTargetingValues = readAuctionQualityTargeting(
                                    managedSlot
                                );
                                var appliedAuctionQualityTargetingValues = null;

                                try {
                                    managedSlot.setTargeting('refresh_count', String(nextRefreshCount));
                                    managedSlot.setTargeting('refresh_bucket', nextRefreshBucket);
                                    managedSlot.setTargeting(
                                        'refresh_interval',
                                        refreshPolicyForTimer.refreshIntervalTargetingValue
                                    );
                                    managedSlot.setTargeting(
                                        'refresh_policy',
                                        refreshPolicyForTimer.refreshPolicyTargetingValue
                                    );

                                    // Built from the auction that has just finished
                                    // for this slot and written immediately before
                                    // the GPT refresh below, so the values describe
                                    // the request they travel with.
                                    appliedAuctionQualityTargetingValues = collectAndApplyAuctionQuality(
                                        managedSlotElementId,
                                        managedSlot,
                                        bidResponses,
                                        timedOut,
                                        auctionId,
                                        'refresh'
                                    );

                                    if (managedSlotElementId === TOP_BILLBOARD_SLOT_ELEMENT_ID) {
                                        isWaitingForBillboardRenderEvent = true;
                                    }

                                    pbjs.setTargetingForGPTAsync([slotElementId]);
                                    googletag.pubads().refresh([managedSlot]);
                                    completedRefreshCount = nextRefreshCount;
                                }

                                catch (error) {
                                    isWaitingForBillboardRenderEvent = false;
                                    isAuctionRunning = false;

                                    managedSlot.setTargeting(
                                        'refresh_count',
                                        String(completedRefreshCount)
                                    );
                                    managedSlot.setTargeting(
                                        'refresh_bucket',
                                        getRefreshBucket(completedRefreshCount)
                                    );
                                    managedSlot.setTargeting(
                                        'refresh_interval',
                                        previousRefreshIntervalTargetingValue
                                    );
                                    managedSlot.setTargeting(
                                        'refresh_policy',
                                        previousRefreshPolicyTargetingValue
                                    );
                                    restoreAuctionQualityTargeting(
                                        managedSlot,
                                        previousAuctionQualityTargetingValues
                                    );

                                    blicRefreshDebug('ERROR', {
                                        slot: managedSlotElementId,
                                        message: 'The GPT refresh request could not be sent.',
                                        error: error,
                                        refreshCount: completedRefreshCount
                                    });

                                    scheduleNextRefresh(slotElementId);
                                    return;
                                }

                                blicRefreshDebug('REFRESH REQUESTED', {
                                    slot: managedSlotElementId,
                                    refreshCount: completedRefreshCount,
                                    refreshBucket: nextRefreshBucket,
                                    refreshInterval: refreshIntervalTargetingValueForTimer,
                                    refreshPolicy: refreshPolicyTargetingValueForTimer,
                                    auctionQuality: appliedAuctionQualityTargetingValues
                                });

                                if (managedSlotElementId === TOP_BILLBOARD_SLOT_ELEMENT_ID) {
                                    startBillboardRenderWatchdog(slotElementId);
                                    return;
                                }

                                isAuctionRunning = false;
                                scheduleNextRefresh(slotElementId); // Schedule next refresh using updated refresh_count.
                            }
                        });
                    }

                    catch (error) {
                        isAuctionRunning = false;

                        blicRefreshDebug('ERROR', {
                            slot: managedSlotElementId,
                            message: 'The Prebid refresh auction could not be started.',
                            error: error,
                            refreshCount: completedRefreshCount
                        });

                        scheduleNextRefresh(slotElementId);
                    }

                }
                else {
                    blicRefreshDebug('BLOCKED', {
                        slot: managedSlotElementId,
                        orderId: currentOrderId,
                        advertiserId: currentAdvertiserId,
                        blockedByOrder: blockedByOrder,
                        blockedByAdvertiser: blockedByAdvertiser,
                        blockedByAdUnit: blockedByAdUnit,
                        tabVisible: browserTabIsVisible,
                        refreshCount: completedRefreshCount
                    });

                    scheduleNextRefresh(slotElementId); // Keep checking at the correct cadence while the slot stays viewable.
                }

            }, refreshDelaySeconds * 1000);
        }

        // Re-evaluate the applicable refresh policy on every render, for every slot:
        // whichever creative just rendered determines the wait until the next refresh,
        // so a slot moves onto (or back off) the 30 second advertiser override immediately.
        googletag.pubads().addEventListener('slotRenderEnded', function(renderEvent) {
            if (renderEvent.slot !== managedSlot) {
                return;
            }

            clearRefreshTimeout();

            if (managedSlotElementId === TOP_BILLBOARD_SLOT_ELEMENT_ID) {
                clearBillboardRenderWatchdog();
                isWaitingForBillboardRenderEvent = false;
            }

            currentSlotAdvertiserId = getNormalizedGAMIdentifier(
                renderEvent.advertiserId
            );
            billboardRefreshPolicyIsReady = true;
            isAuctionRunning = false;

            var renderedRefreshPolicy = getRefreshPolicy(
                managedSlotElementId,
                completedRefreshCount,
                currentSlotAdvertiserId
            );

            blicRefreshDebug('REFRESH POLICY SELECTED', {
                slot: managedSlotElementId,
                orderId: renderEvent.campaignId,
                advertiserId: currentSlotAdvertiserId,
                isEmpty: renderEvent.isEmpty,
                delaySeconds: renderedRefreshPolicy.refreshDelaySeconds,
                refreshPolicy: renderedRefreshPolicy.refreshPolicyTargetingValue,
                refreshCount: completedRefreshCount,
                refreshBucket: getRefreshBucket(completedRefreshCount)
            });

            scheduleNextRefresh(managedSlotElementId);
        });

        if (managedSlotElementId === TOP_BILLBOARD_SLOT_ELEMENT_ID) {
            document.addEventListener('visibilitychange', function() {
                if (document.visibilityState !== 'visible') {
                    clearRefreshTimeout();

                    blicRefreshDebug('TIMER PAUSED', {
                        slot: managedSlotElementId,
                        reason: 'The browser tab became hidden.',
                        refreshCount: completedRefreshCount
                    });
                    return;
                }

                scheduleNextRefresh(managedSlotElementId);
            });
        }

        // Use IntersectionObserver API to check whether an ad slot is viewable for certain percent. 
        var viewabilityObserver = new IntersectionObserver(function(viewabilityEntries) {

            viewabilityEntries.forEach(function(viewabilityEntry) {

                var slotElementId = viewabilityEntry.target.id; // Get the ID of the slot being observed.

                if (viewabilityEntry.isIntersecting) {
                    isSlotInView = true;
                    scheduleNextRefresh(slotElementId);
                }

                else {
                    isSlotInView = false;
                    clearRefreshTimeout(); // If the slot is not intersecting, remove the pending refresh timer.

                    blicRefreshDebug('LEFT VIEW', {
                        slot: managedSlotElementId,
                        refreshCount: completedRefreshCount
                    });
                }
            });

        }, {threshold: VISIBILITY_PERCENT_FOR_REFRESH}); // Percent of the ad slot that has to be viewable to start refresh.

            // Some slots (e.g., interstitial/out-of-page) have no DOM element; also avoid invalid CSS selector ids.
            var viewabilitySlotElement = document.getElementById(managedSlotElementId);

            if (viewabilitySlotElement !== null) {
                viewabilityObserver.observe(viewabilitySlotElement);
            }

    });

}

// Start the wrapper earlier than window.load, but only after this wrapper file has fully initialized.
// This prevents runWrapper() from executing before config variables like adUnitsATF/adUnitsBTF are assigned.
function startAdWrapperEarly() {
    if (!window.prebidAuctionRun) {
        window.prebidAuctionRun = true;
        googletag.cmd.push(function() {
            runWrapper();
        });
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
        startAdWrapperEarly();
    });
} else {
    startAdWrapperEarly();
}
